import {
  EntityCompanyType,
  EntityIndustry,
  EntityLanguage,
  EntitySector,
  EntityBusinessType,
  EntityDebtorStatus,
  EntityCustomerGroup,
  EntityRelatedUnrelated,
  EntityType,
  AccountPurposePurpose,
  EntityMonthlyIncomeRange,
  ManagementPosition,
  BeneficialOwnerJobTitle,
  BeneficialOwnerJob,
  BeneficialOwnerIncomeSource,
  IzinUsahaType
} from '@xendit/id-open-banking-ekyb-common/dist/enums/submission/entity';
import {
  Country,
  Province,
  Dati2,
  Gender,
  MaritalStatus
} from '@xendit/id-open-banking-ekyb-common/dist/enums/submission/common';
import {
  AuthorisedSignatoriesTerms,
  AuthorisedSignatoryPower,
  GetPaginatedSubmissionsFilter
} from '@xendit/id-open-banking-ekyb-common/dist/enums/submission';
import { BeneficialOwnerIncomeSourceType, PrimaryIncomeSourceType } from 'types/override.type';

export const EntityLanguageMap: Record<EntityLanguage, string> = {
  [EntityLanguage.ENGLISH]: 'enums.language.english',
  [EntityLanguage.INDONESIAN]: 'enums.language.indonesian'
};

export const EntityCompanyTypeMap: Record<EntityCompanyType, string> = {
  [EntityCompanyType.VEHICLE_DEALER]: 'enums.entityCompanyType.vehicleDealer',
  [EntityCompanyType.VEHICLE_COMPONENT_MAKER]: 'enums.entityCompanyType.vehicleComponentMaker',
  [EntityCompanyType.TRAVEL_AGENT]: 'enums.entityCompanyType.travelAgent',
  [EntityCompanyType.INTERMEDIARY_DEALER]: 'enums.entityCompanyType.intermediaryDealer',
  [EntityCompanyType.JEWELRY_DEALER]: 'enums.entityCompanyType.jewelryDealer',
  [EntityCompanyType.FIRM_LAW]: 'enums.entityCompanyType.firmLaw',
  [EntityCompanyType.ACCOUNTANT]: 'enums.entityCompanyType.accountant',
  [EntityCompanyType.INVESTMENT_INTERMEDIARY]: 'enums.entityCompanyType.investmentIntermediary',
  [EntityCompanyType.ANTIQUES_AND_ART_DEALER]: 'enums.entityCompanyType.antiquesAndArtDealer',
  [EntityCompanyType.EXIM_TRADING_COMPANY]: 'enums.entityCompanyType.eximTradingCompany',
  [EntityCompanyType.REAL_ESTATE_BROKERAGE_AGENT]: 'enums.entityCompanyType.realEstateBrokerageAgent',
  [EntityCompanyType.PAWNBROKERS]: 'enums.entityCompanyType.pawnbrokers',
  [EntityCompanyType.TELEMARKETER]: 'enums.entityCompanyType.telemarketer',
  [EntityCompanyType.FINANCE_SERVICES_PROVIDER]: 'enums.entityCompanyType.financeServicesProvider'
};

export const EntityIndustryMap: Record<EntityIndustry, string> = {
  [EntityIndustry.PRIVATE_PERSON_NAME]: 'enums.entityIndustry.privatePersonName',
  [EntityIndustry.PRIVATE_PERSON_NUMBERED]: 'enums.entityIndustry.privatePersonNumbered',
  [EntityIndustry.STAFF]: 'enums.entityIndustry.staff',
  [EntityIndustry.NON_PROFIT_INSTITUTIONS]: 'enums.entityIndustry.nonProfitInstitutions',
  [EntityIndustry.PROPERTY_SERVICES]: 'enums.entityIndustry.propertyServices',
  [EntityIndustry.HEALTH_CARE]: 'enums.entityIndustry.healthCare',
  [EntityIndustry.FOOD_AND_TOBACCO]: 'enums.entityIndustry.foodAndTobacco',
  [EntityIndustry.TOBACCO]: 'enums.entityIndustry.tobacco',
  [EntityIndustry.FOOD_MANUFACTURERS]: 'enums.entityIndustry.foodManufacturers',
  [EntityIndustry.WINES_AND_SPIRITS]: 'enums.entityIndustry.winesAndSpirits',
  [EntityIndustry.BREWERIES_and_DISTILLERS]: 'enums.entityIndustry.breweriesAndDistillers',
  [EntityIndustry.SPORT]: 'enums.entityIndustry.sport',
  [EntityIndustry.AGRICULTURE_and_FISHERIES]: 'enums.entityIndustry.agricultureAndFisheries',
  [EntityIndustry.MINING_and_QUARRIES]: 'enums.entityIndustry.miningAndQuarries',
  [EntityIndustry.INDUSTRIAL]: 'enums.entityIndustry.industrial',
  [EntityIndustry.TRADER]: 'enums.entityIndustry.trader',
  [EntityIndustry.COMMERCIAL_TRADER]: 'enums.entityIndustry.commercialTrader',
  [EntityIndustry.INDUSTRIAL_TRADER]: 'enums.entityIndustry.industrialTrader',
  [EntityIndustry.RETAIL_TRADER]: 'enums.entityIndustry.retailTrader',
  [EntityIndustry.BUILDING_AND_CONSTRUCTION]: 'enums.entityIndustry.buildingAndConstruction',
  [EntityIndustry.BUILDING_AND_CONSTRUCTION_DESIGN]: 'enums.entityIndustry.buildingAndConstructionDesign',
  [EntityIndustry.BUILDING_MERCHANTS_AND_SUPPLIERS]: 'enums.entityIndustry.buildingMerchantsAndSuppliers',
  [EntityIndustry.ENGINEERING]: 'enums.entityIndustry.engineering',
  [EntityIndustry.EXTRACTIVE_INDUSTRIES]: 'enums.entityIndustry.extractiveIndustries',
  [EntityIndustry.TEXTILES_AND_APPAREL]: 'enums.entityIndustry.textilesAndApparel',
  [EntityIndustry.PRINTING_PAPER_AND_PACKAGING]: 'enums.entityIndustry.printingPaperAndPackaging',
  [EntityIndustry.DISTRIBUTORS]: 'enums.entityIndustry.distributors',
  [EntityIndustry.DIVERSIFIED_INDUSTRIALS]: 'enums.entityIndustry.diversifiedIndustrials',
  [EntityIndustry.ENERGY]: 'enums.entityIndustry.energy',
  [EntityIndustry.ELECTRICITY_DISTRIBUTION]: 'enums.entityIndustry.electricityDistribution',
  [EntityIndustry.GAS_DISTRIBUTION]: 'enums.entityIndustry.gasDistribution',
  [EntityIndustry.WATER_DISTRIBUTION]: 'enums.entityIndustry.waterDistribution',
  [EntityIndustry.OIL_EXPLORATION_AND_PRODUCTION]: 'enums.entityIndustry.oilExplorationAndProduction',
  [EntityIndustry.OIL_INTEGRATED]: 'enums.entityIndustry.oilIntegrated',
  [EntityIndustry.SERVICE_INDUSTRIES]: 'enums.entityIndustry.serviceIndustries',
  [EntityIndustry.LEISURE_AND_HOTELS]: 'enums.entityIndustry.leisureAndHotels',
  [EntityIndustry.RETAIL_SERVICES]: 'enums.entityIndustry.retailServices',
  [EntityIndustry.MEDIA_INDUSTRIES]: 'enums.entityIndustry.mediaIndustries',
  [EntityIndustry.CHEMICALS]: 'enums.entityIndustry.chemicals',
  [EntityIndustry.PHARMACEUTICALS]: 'enums.entityIndustry.pharmaceuticals',
  [EntityIndustry.TRANSPORT]: 'enums.entityIndustry.transport',
  [EntityIndustry.AIR_TRANSPORT]: 'enums.entityIndustry.airTransport',
  [EntityIndustry.RAIL_TRANSPORT]: 'enums.entityIndustry.railTransport',
  [EntityIndustry.ROAD_TRANSPORT]: 'enums.entityIndustry.roadTransport',
  [EntityIndustry.SEA_TRANSPORT]: 'enums.entityIndustry.seaTransport',
  [EntityIndustry.TELECOMMUNICATIONS]: 'enums.entityIndustry.telecommunications',
  [EntityIndustry.ELECTRONICS]: 'enums.entityIndustry.electronics',
  [EntityIndustry.ELECTRICAL_EQUIPMENT_MANUFACTURING]: 'enums.entityIndustry.electricalEquipmentManufacturing',
  [EntityIndustry.CENTRAL_BANKS]: 'enums.entityIndustry.centralBanks',
  [EntityIndustry.BANKS]: 'enums.entityIndustry.banks',
  [EntityIndustry.MERCHANT_BANKS]: 'enums.entityIndustry.merchantBanks',
  [EntityIndustry.PRIVATE_BANKS]: 'enums.entityIndustry.privateBanks',
  [EntityIndustry.INVESTMENT_BANKS]: 'enums.entityIndustry.investmentBanks',
  [EntityIndustry.DERIVATIVES_TRADER]: 'enums.entityIndustry.derivativesTrader',
  [EntityIndustry.BROKERS_AND_FINANCIAL_ADVISORS]: 'enums.entityIndustry.brokersAndFinancialAdvisors',
  [EntityIndustry.BUILDING_AND_MUTUAL_SOCIETIES]: 'enums.entityIndustry.buildingAndMutualSocieties',
  [EntityIndustry.POSTAL_OFFICE]: 'enums.entityIndustry.postalOffice',
  [EntityIndustry.OTHER_FINANCIAL]: 'enums.entityIndustry.otherFinancial',
  [EntityIndustry.PENSION_FUNDS]: 'enums.entityIndustry.pensionFunds',
  [EntityIndustry.INSURANCE_COMPANIES]: 'enums.entityIndustry.insuranceCompanies',
  [EntityIndustry.PROPERTY_INSURANCE]: 'enums.entityIndustry.propertyInsurance',
  [EntityIndustry.LIFE_ASSURANCE]: 'enums.entityIndustry.lifeAssurance',
  [EntityIndustry.INVESTMENT_FUNDS]: 'enums.entityIndustry.investmentFunds',
  [EntityIndustry.INVESTMENT_TRUSTS]: 'enums.entityIndustry.investmentTrusts',
  [EntityIndustry.INVESTMENT_COMPANIES]: 'enums.entityIndustry.investmentCompanies',
  [EntityIndustry.COMPUTER_SOFTWARE]: 'enums.entityIndustry.computerSoftware',
  [EntityIndustry.COMPUTER_HARDWARE]: 'enums.entityIndustry.computerHardware',
  [EntityIndustry.PUBLIC_SECTOR]: 'enums.entityIndustry.publicSector',
  [EntityIndustry.PUBLIC_INSTITUTIONS]: 'enums.entityIndustry.publicInstitutions',
  [EntityIndustry.INTERNATIONAL_ORGANISATIONS]: 'enums.entityIndustry.internationalOrganisations',
  [EntityIndustry.CENTRAL_GOVERNMENT]: 'enums.entityIndustry.centralGovernment',
  [EntityIndustry.DERIVATIVES]: 'enums.entityIndustry.derivatives',
  [EntityIndustry.MARGIN_TRADING]: 'enums.entityIndustry.marginTrading',
  [EntityIndustry.OTHER]: 'enums.entityIndustry.other'
};

export const EntitySectorMap: Record<EntitySector, string> = {
  [EntitySector.NON_PERSONAL]: 'enums.entitySector.nonPersonal',
  [EntitySector.NON_PERSONAL_GROUP]: 'enums.entitySector.nonPersonalGroup',
  [EntitySector.BANK_INDONESIA]: 'enums.entitySector.bankIndonesia',
  [EntitySector.OTHER_BANK]: 'enums.entitySector.bankLain'
};

export const EntityBusinessTypeMap: Record<EntityBusinessType, string> = {
  [EntityBusinessType.OTHER]: 'enums.entityBusinessType.other',
  [EntityBusinessType.AGRICULTURE_HUNTING_FARMING_FACILITIES]:
    'enums.entityBusinessType.agricultureHuntingFarmingFacilities',
  [EntityBusinessType.FOOD_CROPS_RICE]: 'enums.entityBusinessType.foodCropsRice',
  [EntityBusinessType.FOOD_CROPS_PALAWIJA_NUTS]: 'enums.entityBusinessType.foodCropsPalawijaNuts',
  [EntityBusinessType.FOOD_CROPS_PALAWIJA_TUBERS]: 'enums.entityBusinessType.foodCropsPalawijaTubers',
  [EntityBusinessType.FOOD_CROPS_PALAWIJA_CORN]: 'enums.entityBusinessType.foodCropsPalawijaCorn',
  [EntityBusinessType.FOOD_CROPS_PALAWIJA_OTHER]: 'enums.entityBusinessType.foodCropsPalawijaOther',
  [EntityBusinessType.FOOD_CROPS_HORTICULTURE]: 'enums.entityBusinessType.foodCropsHorticulture',
  [EntityBusinessType.RUBBER_PLANTATION]: 'enums.entityBusinessType.rubberPlantation',
  [EntityBusinessType.COCONUT_PLANTATION]: 'enums.entityBusinessType.coconutPlantation',
  [EntityBusinessType.COFFEE_PLANTATION]: 'enums.entityBusinessType.coffeePlantation',
  [EntityBusinessType.TOBACCO_PLANTATION]: 'enums.entityBusinessType.tobaccoPlantation',
  [EntityBusinessType.OIL_PALM_PLANTATION]: 'enums.entityBusinessType.oilPalmPlantation',
  [EntityBusinessType.PEPPER_PLANTATION]: 'enums.entityBusinessType.pepperPlantation',
  [EntityBusinessType.TEA_PLANTATION]: 'enums.entityBusinessType.teaPlantation',
  [EntityBusinessType.SUGARCANE_PLANTATION]: 'enums.entityBusinessType.sugarcanePlantation',
  [EntityBusinessType.COTTON_PLANTATION]: 'enums.entityBusinessType.cottonPlantation',
  [EntityBusinessType.CLOVE_PLANTATION]: 'enums.entityBusinessType.clovePlantation',
  [EntityBusinessType.VANILLA_PLANTATION]: 'enums.entityBusinessType.vanillaPlantation',
  [EntityBusinessType.PALA_PLANTATION]: 'enums.entityBusinessType.palaPlantation',
  [EntityBusinessType.COCOA_CHOCOLATE_PLANTATION]: 'enums.entityBusinessType.cocoaChocolatePlantation',
  [EntityBusinessType.OTHER_PLANTATIONS]: 'enums.entityBusinessType.otherPlantations',
  [EntityBusinessType.MARINE_FISHING_SHRIMP]: 'enums.entityBusinessType.marineFishingShrimp',
  [EntityBusinessType.MARINE_FISHERIES_OTHERS]: 'enums.entityBusinessType.marineFisheriesOthers',
  [EntityBusinessType.INLAND_FISHING_SHRIMP]: 'enums.entityBusinessType.inlandFishingShrimp',
  [EntityBusinessType.INLAND_FISHING_OTHERS]: 'enums.entityBusinessType.inlandFishingOthers',
  [EntityBusinessType.BRACKISH_FISHERY_SHRIMP]: 'enums.entityBusinessType.brackishFisheryShrimp',
  [EntityBusinessType.BRACKISH_FISHERY_OTHERS]: 'enums.entityBusinessType.brackishFisheryOthers',
  [EntityBusinessType.POULTRY_FARM]: 'enums.entityBusinessType.poultryFarm',
  [EntityBusinessType.CATTLE_FARM]: 'enums.entityBusinessType.cattleFarm',
  [EntityBusinessType.OTHER_FARMS]: 'enums.entityBusinessType.otherFarms',
  [EntityBusinessType.FORESTRY_AND_TIMBER_CUTTING_LOGGING]: 'enums.entityBusinessType.forestryAndTimberCuttingLogging',
  [EntityBusinessType.HUNT]: 'enums.entityBusinessType.hunt',
  [EntityBusinessType.AGRICULTURAL_FACILITIES_PUMPING]: 'enums.entityBusinessType.agriculturalFacilitiesPumping',
  [EntityBusinessType.AGRICULTURAL_FACILITIES_SOIL_CULTIVATION_EQUIPMENT]:
    'enums.entityBusinessType.agriculturalFacilitiesSoilCultivationEquipment',
  [EntityBusinessType.AGRICULTURAL_FACILITIES_WAREHOUSE_CLOTHES_FLOOR]:
    'enums.entityBusinessType.agriculturalFacilitiesWarehouseClothesFloor',
  [EntityBusinessType.AGRICULTURAL_FACILITIES_RICE_FIELD_PRINTING]:
    'enums.entityBusinessType.agriculturalFacilitiesRiceFieldPrinting',
  [EntityBusinessType.FISHING_FACILITIES]: 'enums.entityBusinessType.fishingFacilities',
  [EntityBusinessType.FARM_FACILITIES]: 'enums.entityBusinessType.farmFacilities',
  [EntityBusinessType.FORESTRY_FACILITIES]: 'enums.entityBusinessType.forestryFacilities',
  [EntityBusinessType.OTHER_FACILITIES]: 'enums.entityBusinessType.otherFacilities',
  [EntityBusinessType.MINING]: 'enums.entityBusinessType.mining',
  [EntityBusinessType.OIL_AND_GAS_MINING]: 'enums.entityBusinessType.oilAndGasMining',
  [EntityBusinessType.MINING_METAL_ORE_TIN]: 'enums.entityBusinessType.miningMetalOreTin',
  [EntityBusinessType.MINING_METAL_ORES_NICKEL]: 'enums.entityBusinessType.miningMetalOresNickel',
  [EntityBusinessType.MINING_ORE_BAUXITE]: 'enums.entityBusinessType.miningOreBauxite',
  [EntityBusinessType.MINING_ORE_COPPER]: 'enums.entityBusinessType.miningOreCopper',
  [EntityBusinessType.METAL_ORE_MINING_OTHERS]: 'enums.entityBusinessType.metalOreMiningOthers',
  [EntityBusinessType.COAL_MINING]: 'enums.entityBusinessType.coalMining',
  [EntityBusinessType.MINING_OTHER_MINING_GOODS]: 'enums.entityBusinessType.miningOtherMiningGoods',
  [EntityBusinessType.INDUSTRY]: 'enums.entityBusinessType.industry',
  [EntityBusinessType.INDUSTRY_FLOUR]: 'enums.entityBusinessType.industryFlour',
  [EntityBusinessType.INDUSTRY_SUGAR]: 'enums.entityBusinessType.industrySugar',
  [EntityBusinessType.RICE_MILL_HULLER]: 'enums.entityBusinessType.riceMillHuller',
  [EntityBusinessType.INDUSTRY_CRUDE_PALM_OIL]: 'enums.entityBusinessType.industryCrudePalmOil',
  [EntityBusinessType.INDUSTRY_PALM_SEED_OIL]: 'enums.entityBusinessType.industryPalmSeedOil',
  [EntityBusinessType.INDUSTRY_OTHER_PLANT_OILS]: 'enums.entityBusinessType.industryOtherPlantOils',
  [EntityBusinessType.INDUSTRY_SALT]: 'enums.entityBusinessType.industrySalt',
  [EntityBusinessType.INDUSTRY_BEVERAGES]: 'enums.entityBusinessType.industryBeverages',
  [EntityBusinessType.INDUSTRY_TOBACCO]: 'enums.entityBusinessType.industryTobacco',
  [EntityBusinessType.INDUSTRY_CIGARETTES]: 'enums.entityBusinessType.industryCigarettes',
  [EntityBusinessType.INDUSTRY_OTHER_FOOD]: 'enums.entityBusinessType.industryOtherFood',
  [EntityBusinessType.INDUSTRY_ANIMAL_FEED_AND_FISH]: 'enums.entityBusinessType.industryAnimalFeedAndFish',
  [EntityBusinessType.TEXTILE_INDUSTRY]: 'enums.entityBusinessType.textileIndustry',
  [EntityBusinessType.INDUSTRY_CLOTHING]: 'enums.entityBusinessType.industryClothing',
  [EntityBusinessType.INDUSTRY_LEATHER]: 'enums.entityBusinessType.industryLeather',
  [EntityBusinessType.INDUSTRY_WOOD_MATERIAL]: 'enums.entityBusinessType.industryWoodMaterial',
  [EntityBusinessType.INDUSTRY_WOODEN_FURNITURE]: 'enums.entityBusinessType.industryWoodenFurniture',
  [EntityBusinessType.INDUSTRY_OTHER_WOOD]: 'enums.entityBusinessType.industryOtherWood',
  [EntityBusinessType.INDUSTRY_PAPER_AND_PAPER_PRODUCTS]: 'enums.entityBusinessType.industryPaperAndPaperProducts',
  [EntityBusinessType.INDUSTRY_PRINTING_AND_PUBLISHING]: 'enums.entityBusinessType.industryPrintingAndPublishing',
  [EntityBusinessType.INDUSTRY_PAPER_MATERIALS_PULP]: 'enums.entityBusinessType.industryPaperMaterialsPulp',
  [EntityBusinessType.INDUSTRY_FERTILIZER_PEST_MEDICINE]: 'enums.entityBusinessType.industryFertilizerPestMedicine',
  [EntityBusinessType.PHARMACEUTICAL_INDUSTRY]: 'enums.entityBusinessType.pharmaceuticalIndustry',
  [EntityBusinessType.INDUSTRY_PLASTIC]: 'enums.entityBusinessType.industryPlastic',
  [EntityBusinessType.INDUSTRY_OTHER_CHEMICAL_PRODUCTS]: 'enums.entityBusinessType.industryOtherChemicalProducts',
  [EntityBusinessType.REMILLING_AND_SMOKEHOUSE]: 'enums.entityBusinessType.remillingAndSmokehouse',
  [EntityBusinessType.INDUSTRY_CRUMB_RUBBER]: 'enums.entityBusinessType.industryCrumbRubber',
  [EntityBusinessType.INDUSTRY_OTHER_RUBBER_PRODUCTS]: 'enums.entityBusinessType.industryOtherRubberProducts',
  [EntityBusinessType.INDUSTRY_ESSENTIAL_OILS]: 'enums.entityBusinessType.industryEssentialOils',
  [EntityBusinessType.INDUSTRY_CHEMICALS_OIL_PRODUCTS_COAL_OTHER]:
    'enums.entityBusinessType.industryChemicalsOilProductsCoalOther',
  [EntityBusinessType.NON_METAL_MINING_PRODUCTS_PROCESSING_INDUSTRY_OTHER_THAN_PETROLEUM_AND_COAL_PRODUCTS_CEMENT]:
    'enums.entityBusinessType.nonMetalMiningProductsProcessingIndustryOtherThanPetroleumAndCoalProductsCement',
  [EntityBusinessType.PROCESSING_INDUSTRY_OF_NON_METAL_MINING_PRODUCTS_OTHER_THAN_PETROLEUM_AND_COAL_PRODUCTS_BRICKS_TILE]:
    'enums.entityBusinessType.processingIndustryOfNonMetalMiningProductsOtherThanPetroleumAndCoalProductsBricksTile',
  [EntityBusinessType.NON_METAL_MINING_PRODUCTS_PROCESSING_INDUSTRY_OTHER_THAN_PETROLEUM_AND_COAL_PRODUCTS_CERAMICS]:
    'enums.entityBusinessType.nonMetalMiningProductsProcessingIndustryOtherThanPetroleumAndCoalProductsCeramics',
  [EntityBusinessType.NON_METAL_MINING_PRODUCTS_PROCESSING_INDUSTRY_OTHER_THAN_PETROLEUM_AND_COAL_PRODUCTS_OTHERS]:
    'enums.entityBusinessType.nonMetalMiningProductsProcessingIndustryOtherThanPetroleumAndCoalProductsOthers',
  [EntityBusinessType.BASIC_METAL_INDUSTRY_STEEL]: 'enums.entityBusinessType.basicMetalIndustrySteel',
  [EntityBusinessType.BASE_METAL_INDUSTRY_OTHERS]: 'enums.entityBusinessType.baseMetalIndustryOthers',
  [EntityBusinessType.OVERSEAS_MARITIME_COMPONENT_ASSEMBLY]:
    'enums.entityBusinessType.overseasMaritimeComponentAssembly',
  [EntityBusinessType.OVERSEAS_COMPONENT_ASSEMBLY_AUTOMOTIVE]:
    'enums.entityBusinessType.overseasComponentAssemblyAutomotive',
  [EntityBusinessType.OVERSEAS_COMPONENT_ASSEMBLY_ELECTRONICS]:
    'enums.entityBusinessType.overseasComponentAssemblyElectronics',
  [EntityBusinessType.OVERSEAS_COMPONENT_ASSEMBLY_AGRICULTURAL_TOOLS]:
    'enums.entityBusinessType.overseasComponentAssemblyAgriculturalTools',
  [EntityBusinessType.OTHER_OVERSEAS_COMPONENT_ASSEMBLY]: 'enums.entityBusinessType.otherOverseasComponentAssembly',
  [EntityBusinessType.DOMESTIC_COMPONENT_ASSEMBLY_MARITIME]:
    'enums.entityBusinessType.domesticComponentAssemblyMaritime',
  [EntityBusinessType.DOMESTIC_COMPONENT_ASSEMBLY_AUTOMOTIVE]:
    'enums.entityBusinessType.domesticComponentAssemblyAutomotive',
  [EntityBusinessType.DOMESTIC_COMPONENT_ASSEMBLY_ELECTRONICS]:
    'enums.entityBusinessType.domesticComponentAssemblyElectronics',
  [EntityBusinessType.DOMESTIC_COMPONENT_ASSEMBLY_AGRICULTURAL_TOOLS]:
    'enums.entityBusinessType.domesticComponentAssemblyAgriculturalTools',
  [EntityBusinessType.OTHER_DOMESTIC_COMPONENT_ASSEMBLY]: 'enums.entityBusinessType.otherDomesticComponentAssembly',
  [EntityBusinessType.COMPONENT_MANUFACTURING_MARITIME]: 'enums.entityBusinessType.componentManufacturingMaritime',
  [EntityBusinessType.COMPONENT_MANUFACTURING_AUTOMOTIVE]: 'enums.entityBusinessType.componentManufacturingAutomotive',
  [EntityBusinessType.COMPONENT_MANUFACTURING_ELECTRONICS]:
    'enums.entityBusinessType.componentManufacturingElectronics',
  [EntityBusinessType.COMPONENT_MANUFACTURING_AGRICULTURAL_TOOLS]:
    'enums.entityBusinessType.componentManufacturingAgriculturalTools',
  [EntityBusinessType.OTHER_COMPONENT_MANUFACTURING]: 'enums.entityBusinessType.otherComponentManufacturing',
  [EntityBusinessType.INDUSTRY_OTHERS]: 'enums.entityBusinessType.industryOthers',
  [EntityBusinessType.ELECTRICITY_GAS_AND_WATER]: 'enums.entityBusinessType.electricityGasAndWater',
  [EntityBusinessType.RURAL_ELECTRIC]: 'enums.entityBusinessType.ruralElectric',
  [EntityBusinessType.OTHER_ELECTRICS]: 'enums.entityBusinessType.otherElectrics',
  [EntityBusinessType.GAS]: 'enums.entityBusinessType.gas',
  [EntityBusinessType.WATER]: 'enums.entityBusinessType.water',
  [EntityBusinessType.CONSTRUCTION]: 'enums.entityBusinessType.construction',
  [EntityBusinessType.CONSTRUCTION_BTN_SIMPLE_HOUSING]: 'enums.entityBusinessType.constructionBtnSimpleHousing',
  [EntityBusinessType.CONSTRUCTION_PERUMNAS_SIMPLE_HOUSING]:
    'enums.entityBusinessType.constructionPerumnasSimpleHousing',
  [EntityBusinessType.CONSTRUCTION_ANOTHER_SIMPLE_HOUSING]: 'enums.entityBusinessType.constructionAnotherSimpleHousing',
  [EntityBusinessType.CONSTRUCTION_INPRES_MARKET]: 'enums.entityBusinessType.constructionInpresMarket',
  [EntityBusinessType.CONSTRUCTION_TRANSMIGRATION_SETTLEMENT_LAND_PREPARATION]:
    'enums.entityBusinessType.constructionTransmigrationSettlementLandPreparation',
  [EntityBusinessType.CONSTRUCTION_RICE_FIELD_PRINTING]: 'enums.entityBusinessType.constructionRiceFieldPrinting',
  [EntityBusinessType.CONSTRUCTION_HIGHWAYS_AND_BRIDGES]: 'enums.entityBusinessType.constructionHighwaysAndBridges',
  [EntityBusinessType.CONSTRUCTION_PORT]: 'enums.entityBusinessType.constructionPort',
  [EntityBusinessType.CONSTRUCTION_IRRIGATION]: 'enums.entityBusinessType.constructionIrrigation',
  [EntityBusinessType.CONSTRUCTION_RURAL_ELECTRIC]: 'enums.entityBusinessType.constructionRuralElectric',
  [EntityBusinessType.CONSTRUCTION_OTHER_ELECTRICAL]: 'enums.entityBusinessType.constructionOtherElectrical',
  [EntityBusinessType.CONSTRUCTION_PROJECTS_FINANCED_WITH_LOANS_FROM_FOR_OVERSEAS_PAYMENTS]:
    'enums.entityBusinessType.constructionProjectsFinancedWithLoansFromForOverseasPayments',
  [EntityBusinessType.CONSTRUCTION_OTHER]: 'enums.entityBusinessType.constructionOther',
  [EntityBusinessType.TRADE_RESTAURANT_AND_HOTEL]: 'enums.entityBusinessType.tradeRestaurantAndHotel',
  [EntityBusinessType.PALM_OIL_SEED_RAW_MATERIAL_EXPORT]: 'enums.entityBusinessType.palmOilSeedRawMaterialExport',
  [EntityBusinessType.EXPORT_OF_WOOD_RAW_MATERIALS]: 'enums.entityBusinessType.exportOfWoodRawMaterials',
  [EntityBusinessType.RATTAN_RAW_MATERIAL_EXPORT]: 'enums.entityBusinessType.rattanRawMaterialExport',
  [EntityBusinessType.EXPORT_OF_FOREST_RAW_MATERIALS_OTHER_THAN_WOOD_AND_RATTAN]:
    'enums.entityBusinessType.exportOfForestRawMaterialsOtherThanWoodAndRattan',
  [EntityBusinessType.EXPORT_OF_RAW_MATERIALS_FOR_FOOD_CROPS_PLANTATION]:
    'enums.entityBusinessType.exportOfRawMaterialsForFoodCropsPlantation',
  [EntityBusinessType.EXPORT_OF_LIVE_ANIMAL_RAW_MATERIALS_THEIR_PRODUCTS]:
    'enums.entityBusinessType.exportOfLiveAnimalRawMaterialsTheirProducts',
  [EntityBusinessType.EXPORT_OF_TIN_ORE_RAW_MATERIALS]: 'enums.entityBusinessType.exportOfTinOreRawMaterials',
  [EntityBusinessType.EXPORT_OF_METAL_ORE_RAW_MATERIALS_OTHER_THAN_TIN]:
    'enums.entityBusinessType.exportOfMetalOreRawMaterialsOtherThanTin',
  [EntityBusinessType.COAL_RAW_MATERIAL_EXPORT]: 'enums.entityBusinessType.coalRawMaterialExport',
  [EntityBusinessType.OTHER_RAW_MATERIAL_EXPORT]: 'enums.entityBusinessType.otherRawMaterialExport',
  [EntityBusinessType.EXPORT_OF_SAWN_TIMBER_SEMI_FINISHED_GOODS]:
    'enums.entityBusinessType.exportOfSawnTimberSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_SEMI_FINISHED_GOODS_COFFEE_BEANS]:
    'enums.entityBusinessType.exportOfSemiFinishedGoodsCoffeeBeans',
  [EntityBusinessType.EXPORT_OF_TOBACCO_SEMI_FINISHED_GOODS]:
    'enums.entityBusinessType.exportOfTobaccoSemiFinishedGoods',
  [EntityBusinessType.RUBBER_SEMI_FINISHED_GOODS_EXPORT]: 'enums.entityBusinessType.rubberSemiFinishedGoodsExport',
  [EntityBusinessType.EXPORT_OF_PEPPER_SEMI_FINISHED_GOODS]: 'enums.entityBusinessType.exportOfPepperSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_INTERMEDIATE_GOODS_CRUDE_PALM_OIL]:
    'enums.entityBusinessType.exportOfIntermediateGoodsCrudePalmOil',
  [EntityBusinessType.EXPORT_OF_PALM_OIL_SEMI_FINISHED_GOODS]:
    'enums.entityBusinessType.exportOfPalmOilSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_COPRA_MEAL_SEMI_FINISHED_GOODS]:
    'enums.entityBusinessType.exportOfCopraMealSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_SEMI_FINISHED_GOODS_PRODUCED_BY_MR_FOOD_OTHER_PLANTATIONS]:
    'enums.entityBusinessType.exportOfSemiFinishedGoodsProducedByMrFoodOtherPlantations',
  [EntityBusinessType.EXPORT_OF_PROCESSED_ANIMAL_SEMI_FINISHED_GOODS]:
    'enums.entityBusinessType.exportOfProcessedAnimalSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_OTHER_FOOD_SEMI_FINISHED_GOODS]:
    'enums.entityBusinessType.exportOfOtherFoodSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_MINED_SEMI_FINISHED_GOODS]: 'enums.entityBusinessType.exportOfMinedSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_OTHER_SEMI_FINISHED_GOODS]: 'enums.entityBusinessType.exportOfOtherSemiFinishedGoods',
  [EntityBusinessType.EXPORT_OF_FINISHED_PLYWOOD_SIMILAR_GOODS]:
    'enums.entityBusinessType.exportOfFinishedPlywoodSimilarGoods',
  [EntityBusinessType.EXPORT_OF_FINISHED_TEA]: 'enums.entityBusinessType.exportOfFinishedTea',
  [EntityBusinessType.EXPORT_OF_FINISHED_COFFEE_POWDER]: 'enums.entityBusinessType.exportOfFinishedCoffeePowder',
  [EntityBusinessType.EXPORT_OF_FINISHED_GOODS_FROM_FOOD_CROPS_OTHER_PLANTATIONS]:
    'enums.entityBusinessType.exportOfFinishedGoodsFromFoodCropsOtherPlantations',
  [EntityBusinessType.SHRIMP_FINISHED_GOODS_EXPORT]: 'enums.entityBusinessType.shrimpFinishedGoodsExport',
  [EntityBusinessType.EXPORT_OF_FINISHED_ANIMAL_PRODUCTS_OTHER_THAN_SHRIMP]:
    'enums.entityBusinessType.exportOfFinishedAnimalProductsOtherThanShrimp',
  [EntityBusinessType.EXPORT_OF_FINISHED_WOOD_AND_RATTAN_HANDICRAFTS]:
    'enums.entityBusinessType.exportOfFinishedWoodAndRattanHandicrafts',
  [EntityBusinessType.EXPORT_OF_FINISHED_HANDICRAFTS_OTHER_THAN_WOOD_AND_RATTAN]:
    'enums.entityBusinessType.exportOfFinishedHandicraftsOtherThanWoodAndRattan',
  [EntityBusinessType.EXPORT_OF_TEXTILE_FINISHED_GOODS]: 'enums.entityBusinessType.exportOfTextileFinishedGoods',
  [EntityBusinessType.EXPORT_OF_FINISHED_GOODS_OTHER_THAN_TEXTILES]:
    'enums.entityBusinessType.exportOfFinishedGoodsOtherThanTextiles',
  [EntityBusinessType.OTHER_FINISHED_GOODS_EXPORT]: 'enums.entityBusinessType.otherFinishedGoodsExport',
  [EntityBusinessType.EXPORT_OF_SERVICES_CONSTRUCTION]: 'enums.entityBusinessType.exportOfServicesConstruction',
  [EntityBusinessType.EXPORT_SERVICES_OTHERS]: 'enums.entityBusinessType.exportServicesOthers',
  [EntityBusinessType.IMPORT_FOR_FOREIGN_AID_WHEAT_SEED]: 'enums.entityBusinessType.importForForeignAidWheatSeed',
  [EntityBusinessType.IMPORT_FOR_FOREIGN_AID_RICE]: 'enums.entityBusinessType.importForForeignAidRice',
  [EntityBusinessType.IMPORT_FOR_FOREIGN_AID_KAPAS]: 'enums.entityBusinessType.importForForeignAidKapas',
  [EntityBusinessType.IMPORT_FOR_FOREIGN_AID_YARN]: 'enums.entityBusinessType.importForForeignAidYarn',
  [EntityBusinessType.IMPORT_FOR_FOREIGN_AID_OTHERS]: 'enums.entityBusinessType.importForForeignAidOthers',
  [EntityBusinessType.IMPORTS_NOT_IN_THE_CONTEXT_OF_FOREIGN_AID_FERTILIZER_AND_PEST_MEDICINE]:
    'enums.entityBusinessType.importsNotInTheContextOfForeignAidFertilizerAndPestMedicine',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_BESI_BETON]:
    'enums.entityBusinessType.importsNotForForeignAidBesiBeton',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_NEWSPAPER]:
    'enums.entityBusinessType.importsNotForForeignAidNewspaper',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_CLOVES]: 'enums.entityBusinessType.importsNotForForeignAidCloves',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_RICE]: 'enums.entityBusinessType.importsNotForForeignAidRice',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_SUGAR]: 'enums.entityBusinessType.importsNotForForeignAidSugar',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_WHEAT_SEEDS]:
    'enums.entityBusinessType.importsNotForForeignAidWheatSeeds',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_CORN]: 'enums.entityBusinessType.importsNotForForeignAidCorn',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_SOYBEANS]: 'enums.entityBusinessType.importsNotForForeignAidSoybeans',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_PHARMACY]: 'enums.entityBusinessType.importsNotForForeignAidPharmacy',
  [EntityBusinessType.IMPORTS_NOT_IN_THE_CONTEXT_OF_FOREIGN_AID_KEND_MOTORIZED_SPARE_PARTS]:
    'enums.entityBusinessType.importsNotInTheContextOfForeignAidKendMotorizedSpareParts',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_INDUSTRIAL_SPARE_PARTS]:
    'enums.entityBusinessType.importsNotForForeignAidIndustrialSpareParts',
  [EntityBusinessType.IMPORTS_NOT_FOR_FOREIGN_AID_OTHERS]: 'enums.entityBusinessType.importsNotForForeignAidOthers',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_RICE]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegRice',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_CORN]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegCorn',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_SALT]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegSalt',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_SUGAR_STOCKS]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegSugarStocks',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_WOOD]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegWood',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_RUBBER]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegRubber',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_PALM_TREE]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegPalmTree',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_COTTON]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegCotton',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_COPRA]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegCopra',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_CLOVES]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegCloves',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_LADA]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegLada',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_TOBACCO]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegTobacco',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_COFFEE]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegCoffee',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_TEH]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegTeh',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_LIVING_ANIMALS_THE_RESULTS]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegLivingAnimalsTheResults',
  [EntityBusinessType.PURCHASE_COLLECTION_BRG_TRADE_DLM_NEG_OTHER]:
    'enums.entityBusinessType.purchaseCollectionBrgTradeDlmNegOther',
  [EntityBusinessType.CEMENT_DISTRIBUTION]: 'enums.entityBusinessType.cementDistribution',
  [EntityBusinessType.DISTRIBUTION_OF_FERTILIZERS_PESTICIDES]:
    'enums.entityBusinessType.distributionOfFertilizersPesticides',
  [EntityBusinessType.DISTRIBUTION_OF_COARSE_COTTON]: 'enums.entityBusinessType.distributionOfCoarseCotton',
  [EntityBusinessType.CONCRETE_IRON_DISTRIBUTION]: 'enums.entityBusinessType.concreteIronDistribution',
  [EntityBusinessType.DISTRIBUTION_OF_NEWSPAPER_PAPERS]: 'enums.entityBusinessType.distributionOfNewspaperPapers',
  [EntityBusinessType.RICE_DISTRIBUTION]: 'enums.entityBusinessType.riceDistribution',
  [EntityBusinessType.SUGAR_DISTRIBUTION]: 'enums.entityBusinessType.sugarDistribution',
  [EntityBusinessType.DISTRIBUTION_OF_WHEAT_FLOUR]: 'enums.entityBusinessType.distributionOfWheatFlour',
  [EntityBusinessType.SALT_DISTRIBUTION]: 'enums.entityBusinessType.saltDistribution',
  [EntityBusinessType.OIL_FUEL_DISTRIBUTION]: 'enums.entityBusinessType.oilFuelDistribution',
  [EntityBusinessType.OTHER_DISTRIBUTIONS]: 'enums.entityBusinessType.otherDistributions',
  [EntityBusinessType.RETAIL_TRADE]: 'enums.entityBusinessType.retailTrade',
  [EntityBusinessType.RESTAURANT]: 'enums.entityBusinessType.restaurant',
  [EntityBusinessType.HOTEL]: 'enums.entityBusinessType.hotel',
  [EntityBusinessType.TRANSPORTATION_WAREHOUSING_COMMUNICATIONS]:
    'enums.entityBusinessType.transportationWarehousingCommunications',
  [EntityBusinessType.LAND_PUBLIC_TRANSPORT]: 'enums.entityBusinessType.landPublicTransport',
  [EntityBusinessType.RIVER_PUBLIC_TRANSPORT]: 'enums.entityBusinessType.riverPublicTransport',
  [EntityBusinessType.SEA_PUBLIC_TRANSPORT]: 'enums.entityBusinessType.seaPublicTransport',
  [EntityBusinessType.PUBLIC_AIR_TRANSPORT]: 'enums.entityBusinessType.publicAirTransport',
  [EntityBusinessType.TRAVEL_BUREAU]: 'enums.entityBusinessType.travelBureau',
  [EntityBusinessType.WAREHOUSING]: 'enums.entityBusinessType.warehousing',
  [EntityBusinessType.COMMUNICATION]: 'enums.entityBusinessType.communication',
  [EntityBusinessType.BUSINESS_WORLD_SERVICES]: 'enums.entityBusinessType.businessWorldServices',
  [EntityBusinessType.BUSINESS_WORLD_SERVICES_PERUMNAS_SIMPLE_HOUSING]:
    'enums.entityBusinessType.businessWorldServicesPerumnasSimpleHousing',
  [EntityBusinessType.BUSINESS_WORLD_SERVICES_SIMPLE_HOUSING_OTHER_THAN_PERUMNAS]:
    'enums.entityBusinessType.businessWorldServicesSimpleHousingOtherThanPerumnas',
  [EntityBusinessType.BUSINESS_SERVICES_REAL_ESTATE_INPRES_MARKET]:
    'enums.entityBusinessType.businessServicesRealEstateInpresMarket',
  [EntityBusinessType.BUSINESS_SERVICES_OTHER_REAL_ESTATE]: 'enums.entityBusinessType.businessServicesOtherRealEstate',
  [EntityBusinessType.BUSINESS_SERVICES_PROFESSIONS_OTHER_THAN_DOCTORS]:
    'enums.entityBusinessType.businessServicesProfessionsOtherThanDoctors',
  [EntityBusinessType.BUSINESS_SERVICES_LEASING]: 'enums.entityBusinessType.businessServicesLeasing',
  [EntityBusinessType.BUSINESS_SERVICES_OTHERS]: 'enums.entityBusinessType.businessServicesOthers',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES]: 'enums.entityBusinessType.socialCommunityServices',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES_ENTERTAINMENT_AND_CULTURE]:
    'enums.entityBusinessType.socialCommunityServicesEntertainmentAndCulture',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES_HEALTH_PROFESSION]:
    'enums.entityBusinessType.socialCommunityServicesHealthProfession',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES_HEALTH_CARE_TREATMENT]:
    'enums.entityBusinessType.socialCommunityServicesHealthCareTreatment',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES_EDUCATION_HIGHER_EDUCATION]:
    'enums.entityBusinessType.socialCommunityServicesEducationHigherEducation',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES_OTHER_EDUCATION]:
    'enums.entityBusinessType.socialCommunityServicesOtherEducation',
  [EntityBusinessType.SOCIAL_COMMUNITY_SERVICES_OTHERS]: 'enums.entityBusinessType.socialCommunityServicesOthers',
  [EntityBusinessType.OTHERS_HOUSING]: 'enums.entityBusinessType.othersHousing',
  [EntityBusinessType.OTHERS_VEHICLES]: 'enums.entityBusinessType.othersVehicles',
  [EntityBusinessType.FOR_MORE_THAN_4_WHEELED_MOTOR_VEHICLES]:
    'enums.entityBusinessType.forMoreThan4WheeledMotorVehicles',
  [EntityBusinessType.FOR_4_WHEELED_MOTOR_VEHICLES]: 'enums.entityBusinessType.for4WheeledMotorVehicles',
  [EntityBusinessType.FOR_2_WHEELED_MOTOR_VEHICLES]: 'enums.entityBusinessType.for2WheeledMotorVehicles',
  [EntityBusinessType.OTHERS_HOUSEHOLD_APPLIANCES]: 'enums.entityBusinessType.othersHouseholdAppliances',
  [EntityBusinessType.OTHERS_OTHERS]: 'enums.entityBusinessType.othersOthers'
};

export const EntityDebtorStatusMap: Record<EntityDebtorStatus, string> = {
  [EntityDebtorStatus.MERGER]: 'enums.entityDebtorStatus.merger',
  [EntityDebtorStatus.PARENT_COMPANY]: 'enums.entityDebtorStatus.parentCompany',
  [EntityDebtorStatus.SUBSIDIARY_COMPANY]: 'enums.entityDebtorStatus.subsidiaryCompany',
  [EntityDebtorStatus.OTHER]: 'enums.entityDebtorStatus.other'
};

export const EntityCustomerGroupMap: Record<EntityCustomerGroup, string> = {
  [EntityCustomerGroup.BANK_INDONESIA]: 'enums.entityCustomerGroup.bankIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_PP_OFFICE_OF_THE_TREASURY_AND_STATE_TREASURY_KPKN]:
    'enums.entityCustomerGroup.residentGovernmentSectorPpOfficeOfTheTreasuryAndStateTreasuryKpkn',
  [EntityCustomerGroup.BANK_RAKYAT_INDONESIA]: 'enums.entityCustomerGroup.bankRakyatIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINISTRY_OF_FINANCE]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMinistryOfFinance',
  [EntityCustomerGroup.BANK_EKSPOR_INDONESIA]: 'enums.entityCustomerGroup.bankEksporIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINISTRY_OF_DEFENCE]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMinistryOfDefence',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINISTRY_OF_FORESTRY]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMinistryOfForestry',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINISTRY_OF_AGRICULTURE]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMinistryOfAgriculture',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINING_AND_ENERGY]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMiningAndEnergy',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINISTRY_OF_RELIGION]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMinistryOfReligion',
  [EntityCustomerGroup.BANK_MANDIRI]: 'enums.entityCustomerGroup.bankMandiri',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_MINISTRY_OF_STATE_OWNED_ENTERPRISES]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentMinistryOfStateOwnedEnterprises',
  [EntityCustomerGroup.BANK_NEGARA_INDONESIA_1946]: 'enums.entityCustomerGroup.bankNegaraIndonesia1946',
  [EntityCustomerGroup.MINISTRY_OF_CULTURE_PRIMARY_AND_SECONDARY_EDUCATION]:
    'enums.entityCustomerGroup.ministryOfCulturePrimaryAndSecondaryEducation',
  [EntityCustomerGroup.MINISTRY_OF_PUBLIC_WORKS_AND_PUBLIC_HOUSING]:
    'enums.entityCustomerGroup.ministryOfPublicWorksAndPublicHousing',
  [EntityCustomerGroup.BANK_DANAMON_INDONESIA]: 'enums.entityCustomerGroup.bankDanamonIndonesia',
  [EntityCustomerGroup.MINISTRY_OF_HEALTH]: 'enums.entityCustomerGroup.ministryOfHealth',
  [EntityCustomerGroup.MINISTRY_OF_TRANSPORTATION]: 'enums.entityCustomerGroup.ministryOfTransportation',
  [EntityCustomerGroup.BANK_PERMATA_TBK]: 'enums.entityCustomerGroup.bankPermataTbk',
  [EntityCustomerGroup.PUBLIC_HEALTH_SERVICE_AGENCY]: 'enums.entityCustomerGroup.publicHealthServiceAgency',
  [EntityCustomerGroup.EDUCATION_PUBLIC_SERVICE_AGENCY]: 'enums.entityCustomerGroup.educationPublicServiceAgency',
  [EntityCustomerGroup.BANK_CENTRAL_ASIA_TBK]: 'enums.entityCustomerGroup.bankCentralAsiaTbk',
  [EntityCustomerGroup.BANK_INTERNASIONAL_INDONESIA]: 'enums.entityCustomerGroup.bankInternasionalIndonesia',
  [EntityCustomerGroup.BANK_PANIN_INDONESIA]: 'enums.entityCustomerGroup.bankPaninIndonesia',
  [EntityCustomerGroup.OTHER_PUBLIC_SERVICE_AGENCIES]: 'enums.entityCustomerGroup.otherPublicServiceAgencies',
  [EntityCustomerGroup.BANK_CIMB_NIAGA]: 'enums.entityCustomerGroup.bankCimbNiaga',
  [EntityCustomerGroup.BANK_UOB_BUANA_TBK]: 'enums.entityCustomerGroup.bankUobBuanaTbk',
  [EntityCustomerGroup.BANK_LIPPO]: 'enums.entityCustomerGroup.bankLippo',
  [EntityCustomerGroup.BANK_OCBC_NISP]: 'enums.entityCustomerGroup.bankOcbcNisp',
  [EntityCustomerGroup.CITIBANK]: 'enums.entityCustomerGroup.citibank',
  [EntityCustomerGroup.JP_MORGAN_CHASE_BANK]: 'enums.entityCustomerGroup.jpMorganChaseBank',
  [EntityCustomerGroup.BANK_OF_AMERICA_NT_SA]: 'enums.entityCustomerGroup.bankOfAmericaNtSa',
  [EntityCustomerGroup.BANK_WINDU_KENTJANA_INTERNATIONAL]: 'enums.entityCustomerGroup.bankWinduKentjanaInternational',
  [EntityCustomerGroup.BANK_ARTHA_GRAHA_INTERNASIONAL]: 'enums.entityCustomerGroup.bankArthaGrahaInternasional',
  [EntityCustomerGroup.BANK_CREDIT_AGRICOLE_INDOSUEZ_BUSINESS_LICENSE_REVOKED]:
    'enums.entityCustomerGroup.bankCreditAgricoleIndosuezBusinessLicenseRevoked',
  [EntityCustomerGroup.BANK_BANGKOK_BANK_LTD]: 'enums.entityCustomerGroup.bankBangkokBankLtd',
  [EntityCustomerGroup.THE_HSBC]: 'enums.entityCustomerGroup.theHsbc',
  [EntityCustomerGroup.THE_BANK_OF_TOKYO_MITSUBISHI_UFJ_LTD]:
    'enums.entityCustomerGroup.theBankOfTokyoMitsubishiUfjLtd',
  [EntityCustomerGroup.BANK_SUMITOMO_MITSUI_INDONESIA]: 'enums.entityCustomerGroup.bankSumitomoMitsuiIndonesia',
  [EntityCustomerGroup.BANK_DBS_INDONESIA]: 'enums.entityCustomerGroup.bankDbsIndonesia',
  [EntityCustomerGroup.BANK_RESONA_PERDANIA]: 'enums.entityCustomerGroup.bankResonaPerdania',
  [EntityCustomerGroup.BANK_MIZUHO_INDONESIA]: 'enums.entityCustomerGroup.bankMizuhoIndonesia',
  [EntityCustomerGroup.STANDARD_CHARTERED_BANK]: 'enums.entityCustomerGroup.standardCharteredBank',
  [EntityCustomerGroup.THE_ROYAL_BANK_OF_SCOTLAND_N_V]: 'enums.entityCustomerGroup.theRoyalBankOfScotlandNV',
  [EntityCustomerGroup.BANK_KEPPEL_TATLEE_BUANA_MERGER_TO_BECOME_BANK_OCBC]:
    'enums.entityCustomerGroup.bankKeppelTatleeBuanaMergerToBecomeBankOcbc',
  [EntityCustomerGroup.BANK_CAPITAL_INDONESIA]: 'enums.entityCustomerGroup.bankCapitalIndonesia',
  [EntityCustomerGroup.SAKURA_SWADHARMA_BANK]: 'enums.entityCustomerGroup.sakuraSwadharmaBank',
  [EntityCustomerGroup.BANK_BNP_PARIBAS_INDONESIA]: 'enums.entityCustomerGroup.bankBnpParibasIndonesia',
  [EntityCustomerGroup.BANK_UOB_INDONESIA]: 'enums.entityCustomerGroup.bankUobIndonesia',
  [EntityCustomerGroup.PT_KOREA_EXCHANGE_BANK_DANAMON_MERGER_484]:
    'enums.entityCustomerGroup.ptKoreaExchangeBankDanamonMerger484',
  [EntityCustomerGroup.RABO_BANK_INTERNATIONAL_INDONESIA]: 'enums.entityCustomerGroup.raboBankInternationalIndonesia',
  [EntityCustomerGroup.BANK_ANZ_PANIN]: 'enums.entityCustomerGroup.bankAnzPanin',
  [EntityCustomerGroup.BANK_UPPINDO]: 'enums.entityCustomerGroup.bankUppindo',
  [EntityCustomerGroup.DEUTSCHE_BANK_AG]: 'enums.entityCustomerGroup.deutscheBankAg',
  [EntityCustomerGroup.BANK_WOORI_INDONESIA]: 'enums.entityCustomerGroup.bankWooriIndonesia',
  [EntityCustomerGroup.BANK_OF_CHINA]: 'enums.entityCustomerGroup.bankOfChina',
  [EntityCustomerGroup.BANK_BUMI_ARTA]: 'enums.entityCustomerGroup.bankBumiArta',
  [EntityCustomerGroup.BANK_EKONOMI_RAHARJA]: 'enums.entityCustomerGroup.bankEkonomiRaharja',
  [EntityCustomerGroup.BANK_ANTAR_DAERAH]: 'enums.entityCustomerGroup.bankAntarDaerah',
  [EntityCustomerGroup.RABOBANK_INTERNATIONAL_INDONESIA]: 'enums.entityCustomerGroup.rabobankInternationalIndonesia',
  [EntityCustomerGroup.BANK_IFI]: 'enums.entityCustomerGroup.bankIfi',
  [EntityCustomerGroup.BANK_MUTIARA_TBK]: 'enums.entityCustomerGroup.bankMutiaraTbk',
  [EntityCustomerGroup.BANK_MAYAPADA_INTERNATIONAL]: 'enums.entityCustomerGroup.bankMayapadaInternational',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_CENTRAL_GOVERNMENT_OTHER_DEPARTMENTS]:
    'enums.entityCustomerGroup.residentGovernmentSectorCentralGovernmentOtherDepartments',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_LOCAL_GOVERNMENT_PROVINCE_GOVERNMENT]:
    'enums.entityCustomerGroup.residentGovernmentSectorLocalGovernmentProvinceGovernment',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_LOCAL_GOVERNMENT_CITY_GOVERNMENT]:
    'enums.entityCustomerGroup.residentGovernmentSectorLocalGovernmentCityGovernment',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_LOCAL_GOVERNMENT_DISTRICT_GOVERNMENT]:
    'enums.entityCustomerGroup.residentGovernmentSectorLocalGovernmentDistrictGovernment',
  [EntityCustomerGroup.PROVINCIAL_PUBLIC_SERVICE_AGENCY]: 'enums.entityCustomerGroup.provincialPublicServiceAgency',
  [EntityCustomerGroup.DISTRICT_CITY_PUBLIC_SERVICE_AGENCY]:
    'enums.entityCustomerGroup.districtCityPublicServiceAgency',
  [EntityCustomerGroup.BPD_JAWA_BARAT_AND_BANTEN]: 'enums.entityCustomerGroup.bpdJawaBaratAndBanten',
  [EntityCustomerGroup.PT_BANK_DKI]: 'enums.entityCustomerGroup.ptBankDki',
  [EntityCustomerGroup.PT_BPD_YOGYAKARTA]: 'enums.entityCustomerGroup.ptBpdYogyakarta',
  [EntityCustomerGroup.PT_BPD_JAWA_TENGAH]: 'enums.entityCustomerGroup.ptBpdJawaTengah',
  [EntityCustomerGroup.PT_BPD_JAWA_TIMUR]: 'enums.entityCustomerGroup.ptBpdJawaTimur',
  [EntityCustomerGroup.PT_BPD_JAMBI]: 'enums.entityCustomerGroup.ptBpdJambi',
  [EntityCustomerGroup.PT_BANK_ACEH]: 'enums.entityCustomerGroup.ptBankAceh',
  [EntityCustomerGroup.PT_BPD_SUMATERA_UTARA]: 'enums.entityCustomerGroup.ptBpdSumateraUtara',
  [EntityCustomerGroup.PT_BANK_NAGARI]: 'enums.entityCustomerGroup.ptBankNagari',
  [EntityCustomerGroup.PT_BPD_RIAU_KEPRI]: 'enums.entityCustomerGroup.ptBpdRiauKepri',
  [EntityCustomerGroup.PT_BPD_SUMATERA_SELATAN_DAN_BANGKA_BELITUNG]:
    'enums.entityCustomerGroup.ptBpdSumateraSelatanDanBangkaBelitung',
  [EntityCustomerGroup.PT_BPD_LAMPUNG]: 'enums.entityCustomerGroup.ptBpdLampung',
  [EntityCustomerGroup.PT_BPD_KALIMANTAN_SELATAN]: 'enums.entityCustomerGroup.ptBpdKalimantanSelatan',
  [EntityCustomerGroup.PT_BPD_KALIMANTAN_BARAT]: 'enums.entityCustomerGroup.ptBpdKalimantanBarat',
  [EntityCustomerGroup.PT_BPD_KALIMANTAN_TIMUR]: 'enums.entityCustomerGroup.ptBpdKalimantanTimur',
  [EntityCustomerGroup.PT_BPD_KALIMANTAN_TENGAH]: 'enums.entityCustomerGroup.ptBpdKalimantanTengah',
  [EntityCustomerGroup.PT_BPD_SULAWESI_SELATAN_DAN_SULAWESI_BARAT]:
    'enums.entityCustomerGroup.ptBpdSulawesiSelatanDanSulawesiBarat',
  [EntityCustomerGroup.PT_BPD_SULAWESI_UTARA]: 'enums.entityCustomerGroup.ptBpdSulawesiUtara',
  [EntityCustomerGroup.PT_BPD_NUSA_TENGGARA_BARAT]: 'enums.entityCustomerGroup.ptBpdNusaTenggaraBarat',
  [EntityCustomerGroup.PT_BPD_BALI]: 'enums.entityCustomerGroup.ptBpdBali',
  [EntityCustomerGroup.PT_BPD_NUSA_TENGGARA_TIMUR]: 'enums.entityCustomerGroup.ptBpdNusaTenggaraTimur',
  [EntityCustomerGroup.PT_BPD_MALUKU]: 'enums.entityCustomerGroup.ptBpdMaluku',
  [EntityCustomerGroup.PT_BPD_PAPUA]: 'enums.entityCustomerGroup.ptBpdPapua',
  [EntityCustomerGroup.PT_BPD_BENGKULU]: 'enums.entityCustomerGroup.ptBpdBengkulu',
  [EntityCustomerGroup.PT_BPD_SULAWESI_TENGAH]: 'enums.entityCustomerGroup.ptBpdSulawesiTengah',
  [EntityCustomerGroup.PT_BPD_SULAWESI_TENGGARA]: 'enums.entityCustomerGroup.ptBpdSulawesiTenggara',
  [EntityCustomerGroup.BPD_TIMOR_TIMUR]: 'enums.entityCustomerGroup.bpdTimorTimur',
  [EntityCustomerGroup.BANK_NUSANTARA_PARAHYANGAN]: 'enums.entityCustomerGroup.bankNusantaraParahyangan',
  [EntityCustomerGroup.PT_BANK_OF_INDIA_INDONESIA_TBK]: 'enums.entityCustomerGroup.ptBankOfIndiaIndonesiaTbk',
  [EntityCustomerGroup.BANK_MUAMALAT_INDONESIA]: 'enums.entityCustomerGroup.bankMuamalatIndonesia',
  [EntityCustomerGroup.BANK_MESTIKA_DHARMA]: 'enums.entityCustomerGroup.bankMestikaDharma',
  [EntityCustomerGroup.BANK_SHINHAN]: 'enums.entityCustomerGroup.bankShinhan',
  [EntityCustomerGroup.BANK_SINARMAS]: 'enums.entityCustomerGroup.bankSinarmas',
  [EntityCustomerGroup.BANK_MASPION_INDONESIA]: 'enums.entityCustomerGroup.bankMaspionIndonesia',
  [EntityCustomerGroup.BANK_HAGAKITA]: 'enums.entityCustomerGroup.bankHagakita',
  [EntityCustomerGroup.BANK_INDUSTRI]: 'enums.entityCustomerGroup.bankIndustri',
  [EntityCustomerGroup.BANK_GANESHA]: 'enums.entityCustomerGroup.bankGanesha',
  [EntityCustomerGroup.BANK_ICBC_INDONESIA]: 'enums.entityCustomerGroup.bankIcbcIndonesia',
  [EntityCustomerGroup.BANK_HARMONI_INTERNATIONAL]: 'enums.entityCustomerGroup.bankHarmoniInternational',
  [EntityCustomerGroup.PT_BANK_QNB_INDONESIA_TBK_DH_PT_BANK_QNB_KESAWAN_TBK]:
    'enums.entityCustomerGroup.ptBankQnbIndonesiaTbkDhPtBankQnbKesawanTbk',
  [EntityCustomerGroup.BANK_PIKKO_MERGED_INTO_CENTURY_BANK_095]:
    'enums.entityCustomerGroup.bankPikkoMergedIntoCenturyBank095',
  [EntityCustomerGroup.BANK_TABUNGAN_NEGARA]: 'enums.entityCustomerGroup.bankTabunganNegara',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_GOVERNMENT_AGENCIES_AND_BODIES_AGENCY_BULOG]:
    'enums.entityCustomerGroup.residentGovernmentSectorGovernmentAgenciesAndBodiesAgencyBulog',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_NON_FINANCIAL_INSTITUTION_BULOG]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamNonFinancialInstitutionBulog',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_LPS]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbLps',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_OJK]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamOjk',
  [EntityCustomerGroup.BANK_HS_1906]: 'enums.entityCustomerGroup.bankHs1906',
  [EntityCustomerGroup.BANK_TABUNGAN_PENSIUNAN_NASIONAL]: 'enums.entityCustomerGroup.bankTabunganPensiunanNasional',
  [EntityCustomerGroup.BANK_VICTORIA_SYARIAH]: 'enums.entityCustomerGroup.bankVictoriaSyariah',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_JAMSOSTEK]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceJamsostek',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_TASPEN]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceTaspen',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_JIWASRAYA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceJiwasraya',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_JASA_RAHARJA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceJasaRaharja',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_JASINDO]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceJasindo',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_ASABRI]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceAsabri',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BPJS_KESEHATAN]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBpjsKesehatan',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_INSURANCE_COMPANY_OTHER_INSURANCE]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpInsuranceCompanyOtherInsurance',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PADP_PENSION_FUND]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPadpPensionFund',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_PEMCAM_LKNB_VENTURE_CAPITAL]:
    'enums.entityCustomerGroup.residentGovernmentSectorPemcamLknbVentureCapital',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_FINANCING_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbFinancingCompany',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PSR_SECURITIES_COMPANY_THAT_DOES_NOT_DO_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPsrSecuritiesCompanyThatDoesNotDoMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PSR_SECURITIES_COMPANY_THAT_DOES_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPsrSecuritiesCompanyThatDoesMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PSR_MUTUAL_FUND_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPsrMutualFundCompany',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PSR_INVESTMENT_MANAGER]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPsrInvestmentManager',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PSR_PT_DANAREKSA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPsrPtDanareksa',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_PSR_OTHER]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbPsrOther',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_OTHER_PAWNSHOP]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamOtherPawnshop',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_NON_FINANCIAL_INSTITUTION_PT_POS_INDONESIA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamNonFinancialInstitutionPtPosIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNBL_LEMBAGA_PEMBIAYAAN_EKSPOR_INDONESIA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknblLembagaPembiayaanEksporIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_LKNB_OTHERS]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamLknbOthers',
  [EntityCustomerGroup.BANK_SYARIAH_BRI]: 'enums.entityCustomerGroup.bankSyariahBri',
  [EntityCustomerGroup.PT_BANK_JABAR_BANTEN_SYARIAH]: 'enums.entityCustomerGroup.ptBankJabarBantenSyariah',
  [EntityCustomerGroup.BANK_MEGA_TBK]: 'enums.entityCustomerGroup.bankMegaTbk',
  [EntityCustomerGroup.BANK_BUKOPIN]: 'enums.entityCustomerGroup.bankBukopin',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_KERETA_API_INDONESIA_KAI]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtKeretaApiIndonesiaKai',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_PELAYARAN_NASIONAL_INDONESIA_PELNI]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtPelayaranNasionalIndonesiaPelni',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_PELABUHAN_LAUT_INDONESIA_PELINDO]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtPelabuhanLautIndonesiaPelindo',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_ANGKUTAN_SUNGAI_DANAU_PENYEBERANGAN]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtAngkutanSungaiDanauPenyeberangan',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_ANGKASA_PURA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtAngkasaPura',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_PERKEBUNAN_NUSANTARA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtPerkebunanNusantara',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_PERTAMINA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtPertamina',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_PERUSAHAAN_LISTRIK_NEGARA_PLN]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtPerusahaanListrikNegaraPln',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_KRAK_STEEL]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtKrakSteel',
  [EntityCustomerGroup.BANK_SYARIAH_MANDIRI]: 'enums.entityCustomerGroup.bankSyariahMandiri',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_GARUDA_INDONESIA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtGarudaIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_TELKOM]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtTelkom',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_JASA_MARGA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtJasaMarga',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_TIMAH]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtTimah',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PT_ANEKA_TAMBANG]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPtAnekaTambang',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_PERUSAHAAN_JASA_KONSTRUKSI]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkPerusahaanJasaKonstruksi',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_DIRGANTARA_INDONESIA]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtDirgantaraIndonesia',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_INDUSTRI_KAPAL_INDONESIA]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtIndustriKapalIndonesia',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_PAL_INDONESIA]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtPalIndonesia',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_SUCOFINDO]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtSucofindo',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_RAJAWALI_NUSANTARA_INA]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtRajawaliNusantaraIna',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_PGN]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtPgn',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_BUKIT_ASAM_TBK]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtBukitAsamTbk',
  [EntityCustomerGroup.PENDDK_SEKTOR_PEMERINTAH_BUMN_PEMCAM_PT_PUSRI]:
    'enums.entityCustomerGroup.penddkSektorPemerintahBumnPemcamPtPusri',
  [EntityCustomerGroup.BANK_BISNIS_INTERNASIONAL]: 'enums.entityCustomerGroup.bankBisnisInternasional',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMN_PEMCAM_BLK_OTHER]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumnPemcamBlkOther',
  [EntityCustomerGroup.BANK_ANDARA_SRI_PARTHA]: 'enums.entityCustomerGroup.bankAndaraSriPartha',
  [EntityCustomerGroup.BANK_JASA_JAKARTA]: 'enums.entityCustomerGroup.bankJasaJakarta',
  [EntityCustomerGroup.BANK_HANA]: 'enums.entityCustomerGroup.bankHana',
  [EntityCustomerGroup.PT_BANK_MNC_INTERNASIONAL_TBK_D_H_PT_BANK_ICB_BUMIPUTERA_TBK]:
    'enums.entityCustomerGroup.ptBankMncInternasionalTbkDHPtBankIcbBumiputeraTbk',
  [EntityCustomerGroup.BANK_YUDHA_BHAKTI]: 'enums.entityCustomerGroup.bankYudhaBhakti',
  [EntityCustomerGroup.BANK_MITRANIAGA]: 'enums.entityCustomerGroup.bankMitraniaga',
  [EntityCustomerGroup.PT_BANK_RAKYAT_INDONESIA_AGRONIAGA_TBK]:
    'enums.entityCustomerGroup.ptBankRakyatIndonesiaAgroniagaTbk',
  [EntityCustomerGroup.BANK_SBI_INDONESIA]: 'enums.entityCustomerGroup.bankSbiIndonesia',
  [EntityCustomerGroup.BANK_ROYAL_INDONESIA]: 'enums.entityCustomerGroup.bankRoyalIndonesia',
  [EntityCustomerGroup.BANK_NATIONALNOBU]: 'enums.entityCustomerGroup.bankNationalnobu',
  [EntityCustomerGroup.BANK_ASIATIC_DICABUT_IZIN_USAHA]: 'enums.entityCustomerGroup.bankAsiaticDicabutIzinUsaha',
  [EntityCustomerGroup.BANK_SYARIAH_MEGA_INDONESIA]: 'enums.entityCustomerGroup.bankSyariahMegaIndonesia',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PADP_INSURANCE_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPadpInsuranceCompany',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PADP_INSURANCE_FUND]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPadpInsuranceFund',
  [EntityCustomerGroup.BANK_INA_PERDANA]: 'enums.entityCustomerGroup.bankInaPerdana',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_VENTURE_CAPITAL]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbVentureCapital',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_FINANCING_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbFinancingCompany',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PSR_SECURITIES_COMPANY_THAT_DOES_NOT_DO_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPsrSecuritiesCompanyThatDoesNotDoMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PSR_SECURITIES_COMPANY_THAT_DOES_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPsrSecuritiesCompanyThatDoesMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PSR_MUTUAL_FUND_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPsrMutualFundCompany',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PSR_INVESTMENT_MANAGER]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPsrInvestmentManager',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_PSR_OTHER]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbPsrOther',
  [EntityCustomerGroup.BANK_PANIN_SYARIAH]: 'enums.entityCustomerGroup.bankPaninSyariah',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_LKNB_LAINNYA]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdLknbLainnya',
  [EntityCustomerGroup.BANK_PRIMA_MASTER]: 'enums.entityCustomerGroup.bankPrimaMaster',
  [EntityCustomerGroup.BANK_SYARIAH_BUKOPIN]: 'enums.entityCustomerGroup.bankSyariahBukopin',
  [EntityCustomerGroup.BANK_SAHABAT_SAMPOERNA]: 'enums.entityCustomerGroup.bankSahabatSampoerna',
  [EntityCustomerGroup.BANK_BARCLAYS_INDONESIA]: 'enums.entityCustomerGroup.bankBarclaysIndonesia',
  [EntityCustomerGroup.PT_BANK_DINAR_INDONESIA]: 'enums.entityCustomerGroup.ptBankDinarIndonesia',
  [EntityCustomerGroup.BANK_ANGLOMAS_INTERNASIONAL]: 'enums.entityCustomerGroup.bankAnglomasInternasional',
  [EntityCustomerGroup.BANK_DANPAC_MERGED_INTO_BANK_CENTURY_095]:
    'enums.entityCustomerGroup.bankDanpacMergedIntoBankCentury095',
  [EntityCustomerGroup.BANK_KOSAGRAHA_SEMESTA]: 'enums.entityCustomerGroup.bankKosagrahaSemesta',
  [EntityCustomerGroup.PT_BANK_KESEJAHTERAAN_EKONOMI]: 'enums.entityCustomerGroup.ptBankKesejahteraanEkonomi',
  [EntityCustomerGroup.BANK_BCA_SYARIAH]: 'enums.entityCustomerGroup.bankBcaSyariah',
  [EntityCustomerGroup.BANK_ARTOS_INDONESIA]: 'enums.entityCustomerGroup.bankArtosIndonesia',
  [EntityCustomerGroup.GLOBAL_INTERNATIONAL_BANK_BUSINESS_LICENSE_REVOKED]:
    'enums.entityCustomerGroup.globalInternationalBankBusinessLicenseRevoked',
  [EntityCustomerGroup.PT_BTPN_SYARIAH_D_H_PT_BANK_SAHABAT_PURBA_DANARTA]:
    'enums.entityCustomerGroup.ptBtpnSyariahDHPtBankSahabatPurbaDanarta',
  [EntityCustomerGroup.BANK_MULTI_ARTA_SENTOSA]: 'enums.entityCustomerGroup.bankMultiArtaSentosa',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_NON_FINANCIAL_INSTITUTION_LOCAL_WATER_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdNonFinancialInstitutionLocalWaterCompany',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_NON_FINANCIAL_INSTITUTION_MARKET_AREA_COMPANY]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdNonFinancialInstitutionMarketAreaCompany',
  [EntityCustomerGroup.BANK_RATU]: 'enums.entityCustomerGroup.bankRatu',
  [EntityCustomerGroup.BANK_MAYORA]: 'enums.entityCustomerGroup.bankMayora',
  [EntityCustomerGroup.BANK_INDEX_SELINDO]: 'enums.entityCustomerGroup.bankIndexSelindo',
  [EntityCustomerGroup.BANK_PUNDI_INDONESIA]: 'enums.entityCustomerGroup.bankPundiIndonesia',
  [EntityCustomerGroup.BANK_CENTRATAMA_NASIONAL]: 'enums.entityCustomerGroup.bankCentratamaNasional',
  [EntityCustomerGroup.RESIDENT_GOVERNMENT_SECTOR_BUMD_NON_FINANCIAL_INSTITUTION_OTHER]:
    'enums.entityCustomerGroup.residentGovernmentSectorBumdNonFinancialInstitutionOther',
  [EntityCustomerGroup.BANK_FAMA_INTERNASIONAL]: 'enums.entityCustomerGroup.bankFamaInternasional',
  [EntityCustomerGroup.PT_BANK_MANDIRI_TASPEN_POS]: 'enums.entityCustomerGroup.ptBankMandiriTaspenPos',
  [EntityCustomerGroup.BANK_VICTORIA_INTERNATIONAL]: 'enums.entityCustomerGroup.bankVictoriaInternational',
  [EntityCustomerGroup.BANK_HARDA_INTERNASIONAL]: 'enums.entityCustomerGroup.bankHardaInternasional',
  [EntityCustomerGroup.BPR_KONVENSIONAL]: 'enums.entityCustomerGroup.bprKonvensional',
  [EntityCustomerGroup.BPR_SYARIAH]: 'enums.entityCustomerGroup.bprSyariah',
  [EntityCustomerGroup.BRI_NEW_YORK]: 'enums.entityCustomerGroup.briNewYork',
  [EntityCustomerGroup.BRI_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.briCaymanIsland',
  [EntityCustomerGroup.BANK_MANDIRI_TOKYO]: 'enums.entityCustomerGroup.bankMandiriTokyo',
  [EntityCustomerGroup.BANK_MANDIRI_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.bankMandiriCaymanIsland',
  [EntityCustomerGroup.BANK_MANDIRI_SINGAPORE]: 'enums.entityCustomerGroup.bankMandiriSingapore',
  [EntityCustomerGroup.BANK_MANDIRI_HONGKONG]: 'enums.entityCustomerGroup.bankMandiriHongkong',
  [EntityCustomerGroup.BANK_MANDIRI_COOK_ISLAND]: 'enums.entityCustomerGroup.bankMandiriCookIsland',
  [EntityCustomerGroup.MANDIRI_TIMOR_TIMUR]: 'enums.entityCustomerGroup.mandiriTimorTimur',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PADP_INSURANCE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPadpInsuranceCompany',
  [EntityCustomerGroup.BANK_MANDIRI_SHANGHAI]: 'enums.entityCustomerGroup.bankMandiriShanghai',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PADP_PENSION_FUND]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPadpPensionFund',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_VENTURE_CAPITAL]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalVentureCapital',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_FINANCING_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalFinancingCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PSR_SECURITIES_COMPANY_THAT_DOES_NOT_DO_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPsrSecuritiesCompanyThatDoesNotDoMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PSR_SECURITIES_COMPANY_THAT_DOES_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPsrSecuritiesCompanyThatDoesMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PSR_MUTUAL_FUND_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPsrMutualFundCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PSR_INVESTMENT_MANAGER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPsrInvestmentManager',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_PSR_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalPsrOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_LKNB_OTHER_BMT]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalLknbOtherBmt',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_LKNB_OTHER_KSP_PRIMARY_COOPERATIVE]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalLknbOtherKspPrimaryCooperative',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_LKNB_OTHER_KSP_OTHER_COOPERATIVES]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalLknbOtherKspOtherCooperatives',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_SWASTA_NASIONAL_LKNB_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbSwastaNasionalLknbOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PADP_INSURANCE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPadpInsuranceCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PADP_PENSION_FUND]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPadpPensionFund',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_VENTURE_CAPITAL]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedVentureCapital',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_FINANCING_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedFinancingCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PSR_SECURITIES_COMPANY_THAT_DOES_NOT_DO_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPsrSecuritiesCompanyThatDoesNotDoMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PSR_SECURITIES_COMPANY_THAT_DOES_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPsrSecuritiesCompanyThatDoesMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PSR_MUTUAL_FUND_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPsrMutualFundCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PSR_INVESTMENT_MANAGER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPsrInvestmentManager',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_PSR_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedPsrOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_LKNB_OTHER_BMT]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedLknbOtherBmt',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_LKNB_OTHER_KPLMA_IN_INDONESIA_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedLknbOtherKplmaInIndonesiaOther',
  [EntityCustomerGroup.BNI_NEW_YORK]: 'enums.entityCustomerGroup.bniNewYork',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_MIXED_OTHER_NON_BANK_FINANCIAL_INSTITUTIONS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbMixedOtherNonBankFinancialInstitutions',
  [EntityCustomerGroup.BNI_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.bniCaymanIsland',
  [EntityCustomerGroup.BNI_LONDON]: 'enums.entityCustomerGroup.bniLondon',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PADP_INSURANCE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPadpInsuranceCompany',
  [EntityCustomerGroup.BNI_TOKYO]: 'enums.entityCustomerGroup.bniTokyo',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PADP_PENSION_FUND]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPadpPensionFund',
  [EntityCustomerGroup.BNI_HONGKONG]: 'enums.entityCustomerGroup.bniHongkong',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_VENTURE_CAPITAL]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignVentureCapital',
  [EntityCustomerGroup.BNI_SINGAPORE]: 'enums.entityCustomerGroup.bniSingapore',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_FINANCING_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignFinancingCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PSR_SECURITIES_COMPANY_THAT_DOES_NOT_DO_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPsrSecuritiesCompanyThatDoesNotDoMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PSR_PSR_SECURITIES_COMPANY_THAT_DOES_MUTUAL_FUND_BUSINESS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPsrPsrSecuritiesCompanyThatDoesMutualFundBusiness',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PSR_MUTUAL_FUND_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPsrMutualFundCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PSR_INVESTMENT_MANAGER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPsrInvestmentManager',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_PSR_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignPsrOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_LKNB_OTHER_BMT]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignLknbOtherBmt',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_LKNB_KPLMA_IN_INDONESIA_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignLknbKplmaInIndonesiaOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_LKNB_FOREIGN_OTHER_NON_BANK_FINANCIAL_INSTITUTIONS]:
    'enums.entityCustomerGroup.residentPrivateSectorLknbForeignOtherNonBankFinancialInstitutions',
  [EntityCustomerGroup.BANK_DANAMON_INDONESIA_CAYMAN_ISLAND]:
    'enums.entityCustomerGroup.bankDanamonIndonesiaCaymanIsland',
  [EntityCustomerGroup.BANK_PERMATA_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.bankPermataCaymanIsland',
  [EntityCustomerGroup.BANK_PERMATA_LOS_ANGELES]: 'enums.entityCustomerGroup.bankPermataLosAngeles',
  [EntityCustomerGroup.BCA_NEW_YORK]: 'enums.entityCustomerGroup.bcaNewYork',
  [EntityCustomerGroup.BCA_BAHAMA]: 'enums.entityCustomerGroup.bcaBahama',
  [EntityCustomerGroup.BANK_LIPPO_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.bankLippoCaymanIsland',
  [EntityCustomerGroup.BII_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.biiCaymanIsland',
  [EntityCustomerGroup.BII_COOK_ISLAND]: 'enums.entityCustomerGroup.biiCookIsland',
  [EntityCustomerGroup.BII_BOMBAY_INDIA]: 'enums.entityCustomerGroup.biiBombayIndia',
  [EntityCustomerGroup.BII_PORT_LOUIS_REP_MAURITIUS]: 'enums.entityCustomerGroup.biiPortLouisRepMauritius',
  [EntityCustomerGroup.BANK_PANIN_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.bankPaninCaymanIsland',
  [EntityCustomerGroup.BANK_PANIN_COOK_ISLAND]: 'enums.entityCustomerGroup.bankPaninCookIsland',
  [EntityCustomerGroup.BANK_NIAGA_CAYMAN_ISLAND]: 'enums.entityCustomerGroup.bankNiagaCaymanIsland',
  [EntityCustomerGroup.BANK_NIAGA_LOS_ANGELES]: 'enums.entityCustomerGroup.bankNiagaLosAngeles',
  // [EntityCustomerGroup.PT_BANK_BNI_SYARIAH]: 'enums.entityCustomerGroup.ptBankBniSyariah',
  [EntityCustomerGroup.BANK_MUAMALAT_KUALA_LUMPUR]: 'enums.entityCustomerGroup.bankMuamalatKualaLumpur',
  [EntityCustomerGroup.BANK_SENTRAL_NEGARA_ASING]: 'enums.entityCustomerGroup.bankSentralNegaraAsing',
  [EntityCustomerGroup.PRIME_BANK_LN_TERKAIT_DENGAN_BANK]: 'enums.entityCustomerGroup.primeBankLnTerkaitDenganBank',
  [EntityCustomerGroup.PRIME_BANK_LN_TIDAK_TERKAIT_DENGAN_BANK]:
    'enums.entityCustomerGroup.primeBankLnTidakTerkaitDenganBank',
  [EntityCustomerGroup.NON_PRIME_BANK_LN_TERKAIT_DENGAN_BANK]:
    'enums.entityCustomerGroup.nonPrimeBankLnTerkaitDenganBank',
  [EntityCustomerGroup.NON_PRIME_BANK_LN_TIDAK_TERKAIT_DENGAN_BANK]:
    'enums.entityCustomerGroup.nonPrimeBankLnTidakTerkaitDenganBank',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_COMPANY_AUTOMOTIVE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalCompanyAutomotiveCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_PETROLEUM_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyPetroleumCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_TEXTILE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyTextileCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_TIMBER_COMPANY_HPH]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyTimberCompanyHph',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_CIGARETTE_INDUSTRY_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyCigaretteIndustryCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_FOOD_INDUSTRY_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyFoodIndustryCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_AGRIBUSINESS_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyAgribusinessCompany',
  [EntityCustomerGroup.PENDDK_BLK_SWASTA_NASIONAL_TELECOMMUNICATION_COMPANY]:
    'enums.entityCustomerGroup.penddkBlkSwastaNasionalTelecommunicationCompany',
  [EntityCustomerGroup.PENDDK_BLK_SWASTA_NASIONAL_PROPERTY_AND_REAL_ESTATE_COMPANY]:
    'enums.entityCustomerGroup.penddkBlkSwastaNasionalPropertyAndRealEstateCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_OTHER_CONSTRUCTION_SERVICES_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyOtherConstructionServicesCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_OTHER_COMPANY_OTHER_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalOtherCompanyOtherCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_NON_SAVINGS_OR_LOANS_COOPERATIVES_PRIMARY_COOPERATIVES]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalNonSavingsOrLoansCooperativesPrimaryCooperatives',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_NON_SAVINGS_OR_LOANS_COOPERATIVES_OTHER_COOPERATIVES]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalNonSavingsOrLoansCooperativesOtherCooperatives',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_YBSOK_BADAN_AMIL_ZAKAT_INFAQ_DAN_SHADAQAH]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalYbsokBadanAmilZakatInfaqDanShadaqah',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_YBSOK_EDUCATIONAL_INSTITUTIONS]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalYbsokEducationalInstitutions',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_SWASTA_NASIONAL_YBSOK_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkSwastaNasionalYbsokOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_AUTOMOTIVE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyAutomotiveCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_PETROLEUM_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyPetroleumCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_TEXTILE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyTextileCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_TIMBER_COMPANY_HPH]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyTimberCompanyHph',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_CIGARETTE_INDUSTRY_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyCigaretteIndustryCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_FOOD_INDUSTRY_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyFoodIndustryCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_AGRIBUSINESS_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyAgribusinessCompany',
  [EntityCustomerGroup.PENDDK_BLK_MIXED_TELECOMMUNICATION_COMPANY]:
    'enums.entityCustomerGroup.penddkBlkMixedTelecommunicationCompany',
  [EntityCustomerGroup.PENDDK_BLK_MIXED_PROPERTY_AND_REAL_ESTATE_COMPANY]:
    'enums.entityCustomerGroup.penddkBlkMixedPropertyAndRealEstateCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_OTHER_CONSTRUCTION_SERVICES_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyOtherConstructionServicesCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_OTHER_COMPANY_OTHER_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedOtherCompanyOtherCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_YBSOK_BADAN_AMIL_ZAKAT_INFAQ_DAN_SHADAQAH]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedYbsokBadanAmilZakatInfaqDanShadaqah',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_YBSOK_EDUCATIONAL_INSTITUTIONS]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedYbsokEducationalInstitutions',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_YBSOK_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedYbsokOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_MIXED_REPRESENTATIVE_OFFICE_OF_FOREIGN_OWNED_INSTITUTIONS_IN_INDONESIA]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkMixedRepresentativeOfficeOfForeignOwnedInstitutionsInIndonesia',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_AUTOMOTIVE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyAutomotiveCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_PETROLEUM_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyPetroleumCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_TEXTILE_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyTextileCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_TIMBER_COMPANY_HPH]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyTimberCompanyHph',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_CIGARETTE_INDUSTRY_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyCigaretteIndustryCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_FOOD_INDUSTRY_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyFoodIndustryCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_AGRIBUSINESS_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyAgribusinessCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_OTHER_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyOtherCompany',
  [EntityCustomerGroup.RESIDENT_BLK_FOREIGN_TELECOMMUNICATION_COMPANY]:
    'enums.entityCustomerGroup.residentBlkForeignTelecommunicationCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_OTHER_COMPANY_OTHER_CONSTRUCTION_SERVICES_COMPANY]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignOtherCompanyOtherConstructionServicesCompany',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_YBSOK_BADAN_AMIL_ZAKAT_INFAQ_DAN_SHADAQAH]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignYbsokBadanAmilZakatInfaqDanShadaqah',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_YBSOK_EDUCATIONAL_INSTITUTIONS]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignYbsokEducationalInstitutions',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTORS_BLK_FOREIGN_YBSOK_OTHER]:
    'enums.entityCustomerGroup.residentPrivateSectorsBlkForeignYbsokOther',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_BLK_FOREIGN_YBSOK_REPRESENTATIVE_OFFICE_OF_FOREIGN_OWNED_INSTITUTIONS_IN_INDONESIA]:
    'enums.entityCustomerGroup.residentPrivateSectorBlkForeignYbsokRepresentativeOfficeOfForeignOwnedInstitutionsInIndonesia',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_OTHER_PRIVATE_SECTOR]:
    'enums.entityCustomerGroup.residentPrivateSectorOtherPrivateSector',
  [EntityCustomerGroup.RESIDENT_PRIVATE_SECTOR_NON_FINANCIAL_INSTITUTIONS_INDIVIDUALS]:
    'enums.entityCustomerGroup.residentPrivateSectorNonFinancialInstitutionsIndividuals',
  [EntityCustomerGroup.NON_RESIDENT_CENTRAL_GOVERNMENT]: 'enums.entityCustomerGroup.nonResidentCentralGovernment',
  [EntityCustomerGroup.NON_RESIDENT_LOCAL_GOVERNMENT]: 'enums.entityCustomerGroup.nonResidentLocalGovernment',
  [EntityCustomerGroup.NON_RESIDENT_REPRESENTATIVE_OF_FOREIGN_COUNTRIES_AND_THEIR_STAFF]:
    'enums.entityCustomerGroup.nonResidentRepresentativeOfForeignCountriesAndTheirStaff',
  [EntityCustomerGroup.NON_RESIDENT_BUMN_OWNED_BY_FOREIGN_COUNTRY]:
    'enums.entityCustomerGroup.nonResidentBumnOwnedByForeignCountry',
  [EntityCustomerGroup.NON_RESIDENT_NON_BANK_FINANCIAL_INSTITUTIONS_OPERATING_OUTSIDE_INDONESIA]:
    'enums.entityCustomerGroup.nonResidentNonBankFinancialInstitutionsOperatingOutsideIndonesia',
  [EntityCustomerGroup.NON_RESIDENT_FOREIGN_STATE_OWNED_NON_BANK_FINANCIAL_INSTITUTIONS]:
    'enums.entityCustomerGroup.nonResidentForeignStateOwnedNonBankFinancialInstitutions',
  [EntityCustomerGroup.BANK_FINCONESIA]: 'enums.entityCustomerGroup.bankFinconesia',
  [EntityCustomerGroup.BANK_MERINCORP_BUSINESS_LICENSE_REVOKED]:
    'enums.entityCustomerGroup.bankMerincorpBusinessLicenseRevoked',
  [EntityCustomerGroup.MAYBANK_INDOCORP]: 'enums.entityCustomerGroup.maybankIndocorp',
  [EntityCustomerGroup.BANK_OCBC_INDONESIA]: 'enums.entityCustomerGroup.bankOcbcIndonesia',
  [EntityCustomerGroup.PT_BANK_CTBC_INDONESIA]: 'enums.entityCustomerGroup.ptBankCtbcIndonesia',
  [EntityCustomerGroup.BANK_COMMONWEALTH]: 'enums.entityCustomerGroup.bankCommonwealth',
  [EntityCustomerGroup.NON_RESIDENT_OTHER_PRIVATE_PRIVATE_JOINT_VENTURE_INDONESIA_AND_THE_STATE]:
    'enums.entityCustomerGroup.nonResidentOtherPrivatePrivateJointVentureIndonesiaAndTheState',
  [EntityCustomerGroup.NON_RESIDENT_OTHER_PRIVATE_PRIVATE_INDONESIA_OWNED]:
    'enums.entityCustomerGroup.nonResidentOtherPrivatePrivateIndonesiaOwned',
  [EntityCustomerGroup.NON_RESIDENT_OTHER_PRIVATE_OTHER]: 'enums.entityCustomerGroup.nonResidentOtherPrivateOther',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTI_ISLAMIC_DEVELOPMENT_BANK]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultiIslamicDevelopmentBank',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTI_ASIAN_DEVELOPMENT_BANK]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultiAsianDevelopmentBank',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTI_WORLD_BANK]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultiWorldBank',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_AFDB]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralAfdb',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_EBRD]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralEbrd',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_IADB]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralIadb',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_EIB]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralEib',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_EIF]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralEif',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_NIB]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralNib',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_CDB]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralCdb',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTILATERAL_CEDB]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultilateralCedb',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_DEVELOPMENT_BANK_MULTI_OTHER]:
    'enums.entityCustomerGroup.nonResidentInternationalDevelopmentBankMultiOther',
  [EntityCustomerGroup.NON_RESIDENT_INTERNATIONAL_ORGANISATIONS_OTHERS]:
    'enums.entityCustomerGroup.nonResidentInternationalOrganisationsOthers',
  [EntityCustomerGroup.NON_RESIDENT_INDIVIDUAL]: 'enums.entityCustomerGroup.nonResidentIndividual'
};

export const EntityRelatedUnrelatedMap: Record<EntityRelatedUnrelated, string> = {
  [EntityRelatedUnrelated.RELATED]: 'enums.entityRelatedUnrelated.related',
  [EntityRelatedUnrelated.UNRELATED]: 'enums.entityRelatedUnrelated.unrelated'
};

export const CountryMap: Record<Country, string> = {
  [Country.AD]: 'enums.country.ad',
  [Country.AE]: 'enums.country.ae',
  [Country.AF]: 'enums.country.af',
  [Country.AG]: 'enums.country.ag',
  [Country.AI]: 'enums.country.ai',
  [Country.AL]: 'enums.country.al',
  [Country.AM]: 'enums.country.am',
  [Country.AN]: 'enums.country.an',
  [Country.AO]: 'enums.country.ao',
  [Country.AQ]: 'enums.country.aq',
  [Country.AR]: 'enums.country.ar',
  [Country.AS]: 'enums.country.as',
  [Country.AT]: 'enums.country.at',
  [Country.AU]: 'enums.country.au',
  [Country.AW]: 'enums.country.aw',
  [Country.AX]: 'enums.country.ax',
  [Country.AZ]: 'enums.country.az',
  [Country.BA]: 'enums.country.ba',
  [Country.BB]: 'enums.country.bb',
  [Country.BD]: 'enums.country.bd',
  [Country.BE]: 'enums.country.be',
  [Country.BF]: 'enums.country.bf',
  [Country.BG]: 'enums.country.bg',
  [Country.BH]: 'enums.country.bh',
  [Country.BI]: 'enums.country.bi',
  [Country.BJ]: 'enums.country.bj',
  [Country.BL]: 'enums.country.bl',
  [Country.BM]: 'enums.country.bm',
  [Country.BN]: 'enums.country.bn',
  [Country.BO]: 'enums.country.bo',
  [Country.BQ]: 'enums.country.bq',
  [Country.BR]: 'enums.country.br',
  [Country.BS]: 'enums.country.bs',
  [Country.BT]: 'enums.country.bt',
  [Country.BV]: 'enums.country.bv',
  [Country.BW]: 'enums.country.bw',
  [Country.BY]: 'enums.country.by',
  [Country.BZ]: 'enums.country.bz',
  [Country.CA]: 'enums.country.ca',
  [Country.CC]: 'enums.country.cc',
  [Country.CD]: 'enums.country.cd',
  [Country.CF]: 'enums.country.cf',
  [Country.CG]: 'enums.country.cg',
  [Country.CH]: 'enums.country.ch',
  [Country.CI]: 'enums.country.ci',
  [Country.CK]: 'enums.country.ck',
  [Country.CL]: 'enums.country.cl',
  [Country.CM]: 'enums.country.cm',
  [Country.CN]: 'enums.country.cn',
  [Country.CO]: 'enums.country.co',
  [Country.CR]: 'enums.country.cr',
  [Country.CU]: 'enums.country.cu',
  [Country.CV]: 'enums.country.cv',
  [Country.CW]: 'enums.country.cw',
  [Country.CX]: 'enums.country.cx',
  [Country.CY]: 'enums.country.cy',
  [Country.CZ]: 'enums.country.cz',
  [Country.DE]: 'enums.country.de',
  [Country.DJ]: 'enums.country.dj',
  [Country.DK]: 'enums.country.dk',
  [Country.DM]: 'enums.country.dm',
  [Country.DO]: 'enums.country.do',
  [Country.DZ]: 'enums.country.dz',
  [Country.EC]: 'enums.country.ec',
  [Country.EE]: 'enums.country.ee',
  [Country.EG]: 'enums.country.eg',
  [Country.EH]: 'enums.country.eh',
  [Country.ER]: 'enums.country.er',
  [Country.ES]: 'enums.country.es',
  [Country.ET]: 'enums.country.et',
  [Country.EU]: 'enums.country.eu',
  [Country.FI]: 'enums.country.fi',
  [Country.FJ]: 'enums.country.fj',
  [Country.FK]: 'enums.country.fk',
  [Country.FM]: 'enums.country.fm',
  [Country.FO]: 'enums.country.fo',
  [Country.FR]: 'enums.country.fr',
  [Country.GA]: 'enums.country.ga',
  [Country.GB]: 'enums.country.gb',
  [Country.GD]: 'enums.country.gd',
  [Country.GE]: 'enums.country.ge',
  [Country.GF]: 'enums.country.gf',
  [Country.GG]: 'enums.country.gg',
  [Country.GH]: 'enums.country.gh',
  [Country.GI]: 'enums.country.gi',
  [Country.GL]: 'enums.country.gl',
  [Country.GM]: 'enums.country.gm',
  [Country.GN]: 'enums.country.gn',
  [Country.GP]: 'enums.country.gp',
  [Country.GQ]: 'enums.country.gq',
  [Country.GR]: 'enums.country.gr',
  [Country.GS]: 'enums.country.gs',
  [Country.GT]: 'enums.country.gt',
  [Country.GU]: 'enums.country.gu',
  [Country.GW]: 'enums.country.gw',
  [Country.GY]: 'enums.country.gy',
  [Country.HK]: 'enums.country.hk',
  [Country.HM]: 'enums.country.hm',
  [Country.HN]: 'enums.country.hn',
  [Country.HR]: 'enums.country.hr',
  [Country.HT]: 'enums.country.ht',
  [Country.HU]: 'enums.country.hu',
  [Country.ID]: 'enums.country.id',
  [Country.IE]: 'enums.country.ie',
  [Country.IL]: 'enums.country.il',
  [Country.IM]: 'enums.country.im',
  [Country.IN]: 'enums.country.in',
  [Country.IO]: 'enums.country.io',
  [Country.IQ]: 'enums.country.iq',
  [Country.IR]: 'enums.country.ir',
  [Country.IS]: 'enums.country.is',
  [Country.IT]: 'enums.country.it',
  [Country.JE]: 'enums.country.je',
  [Country.JM]: 'enums.country.jm',
  [Country.JO]: 'enums.country.jo',
  [Country.KE]: 'enums.country.ke',
  [Country.KG]: 'enums.country.kg',
  [Country.KH]: 'enums.country.kh',
  [Country.KI]: 'enums.country.ki',
  [Country.KM]: 'enums.country.km',
  [Country.KN]: 'enums.country.kn',
  [Country.KP]: 'enums.country.kp',
  [Country.KR]: 'enums.country.kr',
  [Country.KW]: 'enums.country.kw',
  [Country.KY]: 'enums.country.ky',
  [Country.KZ]: 'enums.country.kz',
  [Country.LA]: 'enums.country.la',
  [Country.LB]: 'enums.country.lb',
  [Country.LC]: 'enums.country.lc',
  [Country.LI]: 'enums.country.li',
  [Country.LK]: 'enums.country.lk',
  [Country.LR]: 'enums.country.lr',
  [Country.LS]: 'enums.country.ls',
  [Country.LT]: 'enums.country.lt',
  [Country.LU]: 'enums.country.lu',
  [Country.LV]: 'enums.country.lv',
  [Country.LY]: 'enums.country.ly',
  [Country.MA]: 'enums.country.ma',
  [Country.MC]: 'enums.country.mc',
  [Country.MD]: 'enums.country.md',
  [Country.ME]: 'enums.country.me',
  [Country.MF]: 'enums.country.mf',
  [Country.MG]: 'enums.country.mg',
  [Country.MH]: 'enums.country.mh',
  [Country.MK]: 'enums.country.mk',
  [Country.ML]: 'enums.country.ml',
  [Country.MM]: 'enums.country.mm',
  [Country.MN]: 'enums.country.mn',
  [Country.MO]: 'enums.country.mo',
  [Country.MP]: 'enums.country.mp',
  [Country.MQ]: 'enums.country.mq',
  [Country.MR]: 'enums.country.mr',
  [Country.MS]: 'enums.country.ms',
  [Country.MT]: 'enums.country.mt',
  [Country.MU]: 'enums.country.mu',
  [Country.MV]: 'enums.country.mv',
  [Country.MW]: 'enums.country.mw',
  [Country.MX]: 'enums.country.mx',
  [Country.MY]: 'enums.country.my',
  [Country.MZ]: 'enums.country.mz',
  [Country.NA]: 'enums.country.na',
  [Country.NC]: 'enums.country.nc',
  [Country.NE]: 'enums.country.ne',
  [Country.NF]: 'enums.country.nf',
  [Country.NG]: 'enums.country.ng',
  [Country.NI]: 'enums.country.ni',
  [Country.NL]: 'enums.country.nl',
  [Country.NO]: 'enums.country.no',
  [Country.NP]: 'enums.country.np',
  [Country.NR]: 'enums.country.nr',
  [Country.NU]: 'enums.country.nu',
  [Country.NZ]: 'enums.country.nz',
  [Country.OM]: 'enums.country.om',
  [Country.PA]: 'enums.country.pa',
  [Country.PE]: 'enums.country.pe',
  [Country.PF]: 'enums.country.pf',
  [Country.PG]: 'enums.country.pg',
  [Country.PH]: 'enums.country.ph',
  [Country.PK]: 'enums.country.pk',
  [Country.PL]: 'enums.country.pl',
  [Country.PM]: 'enums.country.pm',
  [Country.PN]: 'enums.country.pn',
  [Country.PR]: 'enums.country.pr',
  [Country.PS]: 'enums.country.ps',
  [Country.PT]: 'enums.country.pt',
  [Country.PW]: 'enums.country.pw',
  [Country.PY]: 'enums.country.py',
  [Country.QA]: 'enums.country.qa',
  [Country.RE]: 'enums.country.re',
  [Country.RO]: 'enums.country.ro',
  [Country.RS]: 'enums.country.rs',
  [Country.RU]: 'enums.country.ru',
  [Country.RW]: 'enums.country.rw',
  [Country.SA]: 'enums.country.sa',
  [Country.SB]: 'enums.country.sb',
  [Country.SC]: 'enums.country.sc',
  [Country.SD]: 'enums.country.sd',
  [Country.SE]: 'enums.country.se',
  [Country.SG]: 'enums.country.sg',
  [Country.SH]: 'enums.country.sh',
  [Country.SI]: 'enums.country.si',
  [Country.SJ]: 'enums.country.sj',
  [Country.SK]: 'enums.country.sk',
  [Country.SL]: 'enums.country.sl',
  [Country.SM]: 'enums.country.sm',
  [Country.SN]: 'enums.country.sn',
  [Country.SO]: 'enums.country.so',
  [Country.SR]: 'enums.country.sr',
  [Country.SS]: 'enums.country.ss',
  [Country.ST]: 'enums.country.st',
  [Country.SV]: 'enums.country.sv',
  [Country.SX]: 'enums.country.sx',
  [Country.SY]: 'enums.country.sy',
  [Country.SZ]: 'enums.country.sz',
  [Country.TC]: 'enums.country.tc',
  [Country.TD]: 'enums.country.td',
  [Country.TF]: 'enums.country.tf',
  [Country.TG]: 'enums.country.tg',
  [Country.TH]: 'enums.country.th',
  [Country.TJ]: 'enums.country.tj',
  [Country.TK]: 'enums.country.tk',
  [Country.TL]: 'enums.country.tl',
  [Country.TM]: 'enums.country.tm',
  [Country.TN]: 'enums.country.tn',
  [Country.TO]: 'enums.country.to',
  [Country.TP]: 'enums.country.tp',
  [Country.TR]: 'enums.country.tr',
  [Country.TT]: 'enums.country.tt',
  [Country.TV]: 'enums.country.tv',
  [Country.TW]: 'enums.country.tw',
  [Country.TZ]: 'enums.country.tz',
  [Country.UA]: 'enums.country.ua',
  [Country.UG]: 'enums.country.ug',
  [Country.UM]: 'enums.country.um',
  [Country.US]: 'enums.country.us',
  [Country.UY]: 'enums.country.uy',
  [Country.UZ]: 'enums.country.uz',
  [Country.VA]: 'enums.country.va',
  [Country.VC]: 'enums.country.vc',
  [Country.VE]: 'enums.country.ve',
  [Country.VG]: 'enums.country.vg',
  [Country.VI]: 'enums.country.vi',
  [Country.VN]: 'enums.country.vn',
  [Country.VU]: 'enums.country.vu',
  [Country.WF]: 'enums.country.wf',
  [Country.WS]: 'enums.country.ws',
  [Country.XA]: 'enums.country.xa',
  [Country.XE]: 'enums.country.xe',
  [Country.XG]: 'enums.country.xg',
  [Country.XP]: 'enums.country.xp',
  [Country.XS]: 'enums.country.xs',
  [Country.XX]: 'enums.country.xx',
  [Country.YE]: 'enums.country.ye',
  [Country.YT]: 'enums.country.yt',
  [Country.YU]: 'enums.country.yu',
  [Country.ZA]: 'enums.country.za',
  [Country.ZM]: 'enums.country.zm',
  [Country.ZW]: 'enums.country.zw'
};

export const ProvinceMap: Record<Province, string> = {
  [Province.WEST_JAVA]: 'enums.province.westJava',
  [Province.BANTEN]: 'enums.province.banten',
  [Province.DKI_JAKARTA]: 'enums.province.dkiJakarta',
  [Province.D_I_JOGJAKARTA]: 'enums.province.dIJogjakarta',
  [Province.CENTRAL_JAVA]: 'enums.province.centralJava',
  [Province.EAST_JAVA]: 'enums.province.eastJava',
  [Province.BENGKULU]: 'enums.province.bengkulu',
  [Province.JAMBI]: 'enums.province.jambi',
  [Province.NAGGROE_ACEH_DARUSSALAM]: 'enums.province.naggroeAcehDarussalam',
  [Province.NORTH_SUMATRA]: 'enums.province.northSumatra',
  [Province.WEST_SUMATRA]: 'enums.province.westSumatra',
  [Province.RIAU]: 'enums.province.riau',
  [Province.SOUTH_SUMATRA]: 'enums.province.southSumatra',
  [Province.BANGKA_BELITUNG_ISLANDS]: 'enums.province.bangkaBelitungIslands',
  [Province.LAMPUNG]: 'enums.province.lampung',
  [Province.SOUTH_KALIMANTAN]: 'enums.province.southKalimantan',
  [Province.WEST_KALIMANTAN]: 'enums.province.westKalimantan',
  [Province.EAST_KALIMANTAN]: 'enums.province.eastKalimantan',
  [Province.CENTRAL_KALIMANTAN]: 'enums.province.centralKalimantan',
  [Province.CENTRAL_SULAWESI]: 'enums.province.centralSulawesi',
  [Province.SOUTH_SULAWESI]: 'enums.province.southSulawesi',
  [Province.NORTH_SULAWESI]: 'enums.province.northSulawesi',
  [Province.GORONTALO]: 'enums.province.gorontalo',
  [Province.SOUTHEAST_SULAWESI]: 'enums.province.southeastSulawesi',
  [Province.WEST_NUSA_TENGGARA]: 'enums.province.westNusaTenggara',
  [Province.BALI]: 'enums.province.bali',
  [Province.EAST_NUSA_TENGGARA]: 'enums.province.eastNusaTenggara',
  [Province.MALUKU]: 'enums.province.maluku',
  [Province.NORTH_MALUKU]: 'enums.province.northMaluku',
  [Province.RIAU_ISLANDS]: 'enums.province.riauIslands',
  [Province.WEST_SULAWESI]: 'enums.province.westSulawesi',
  [Province.WEST_PAPUA]: 'enums.province.westPapua',
  [Province.PAPUA]: 'enums.province.papua',
  [Province.NORTH_KALIMANTAN]: 'enums.province.northKalimantan',
  [Province.OTHER]: 'enums.province.other'
};

export const Dati2Map: Record<Dati2, string> = {
  [Dati2.BEKASI_KAB]: 'enums.dati2.bekasiKab',
  [Dati2.PURWAKARTA_KAB]: 'enums.dati2.purwakartaKab',
  [Dati2.KARAWANG_KAB]: 'enums.dati2.karawangKab',
  [Dati2.BOGOR_KAB]: 'enums.dati2.bogorKab',
  [Dati2.SUKABUMI_KAB]: 'enums.dati2.sukabumiKab',
  [Dati2.CIANJUR_KAB]: 'enums.dati2.cianjurKab',
  [Dati2.BANDUNG_KAB]: 'enums.dati2.bandungKab',
  [Dati2.SUMEDANG_KAB]: 'enums.dati2.sumedangKab',
  [Dati2.TASIKMALAYA_KAB]: 'enums.dati2.tasikmalayaKab',
  [Dati2.GARUT_KAB]: 'enums.dati2.garutKab',
  [Dati2.CIAMIS_KAB]: 'enums.dati2.ciamisKab',
  [Dati2.CIREBON_KAB]: 'enums.dati2.cirebonKab',
  [Dati2.KUNINGAN_KAB]: 'enums.dati2.kuninganKab',
  [Dati2.INDRAMAYU_KAB]: 'enums.dati2.indramayuKab',
  [Dati2.MAJALENGKA_KAB]: 'enums.dati2.majalengkaKab',
  [Dati2.SUBANG_KAB]: 'enums.dati2.subangKab',
  [Dati2.BANDUNG_BARAT_KAB]: 'enums.dati2.bandungBaratKab',
  [Dati2.KAB_PANGANDARAN]: 'enums.dati2.kabPangandaran',
  [Dati2.BANJAR_KOTA]: 'enums.dati2.banjarKota',
  [Dati2.PROV_JAWA_BARAT_KAB_KOTA_LAIN]: 'enums.dati2.provJawaBaratKabKotaLain',
  [Dati2.BANDUNG_KOTA]: 'enums.dati2.bandungKota',
  [Dati2.BOGOR_KOTA]: 'enums.dati2.bogorKota',
  [Dati2.SUKABUMI_KOTA]: 'enums.dati2.sukabumiKota',
  [Dati2.CIREBON_KOTA]: 'enums.dati2.cirebonKota',
  [Dati2.TASIKMALAYA_KOTA]: 'enums.dati2.tasikmalayaKota',
  [Dati2.CIMAHI_KOTA]: 'enums.dati2.cimahiKota',
  [Dati2.DEPOK_KOTA]: 'enums.dati2.depokKota',
  [Dati2.BEKASI_KOTA]: 'enums.dati2.bekasiKota',
  [Dati2.LEBAK_KAB]: 'enums.dati2.lebakKab',
  [Dati2.PANDEGLANG_KAB]: 'enums.dati2.pandeglangKab',
  [Dati2.SERANG_KAB]: 'enums.dati2.serangKab',
  [Dati2.TANGERANG_KAB]: 'enums.dati2.tangerangKab',
  [Dati2.PROV_BANTEN_KAB_KOTA_LAINNYA]: 'enums.dati2.provBantenKabKotaLainnya',
  [Dati2.CILEGON_KOTA]: 'enums.dati2.cilegonKota',
  [Dati2.TANGERANG_KOTA]: 'enums.dati2.tangerangKota',
  [Dati2.SERANG_KOTA]: 'enums.dati2.serangKota',
  [Dati2.KOTA_TANGERANG_SELATAN]: 'enums.dati2.kotaTangerangSelatan',
  [Dati2.JAKARTA_PUSAT_WIL_KOTA]: 'enums.dati2.jakartaPusatWilKota',
  [Dati2.JAKARTA_UTARA_WIL_KOTA]: 'enums.dati2.jakartaUtaraWilKota',
  [Dati2.JAKARTA_BARAT_WIL_KOTA]: 'enums.dati2.jakartaBaratWilKota',
  [Dati2.JAKARTA_SELATAN_WIL_KOTA]: 'enums.dati2.jakartaSelatanWilKota',
  [Dati2.JAKARTA_TIMUR_WIL_KOTA]: 'enums.dati2.jakartaTimurWilKota',
  [Dati2.KEPULAUAN_SERIBU_WILAYAH]: 'enums.dati2.kepulauanSeribuWilayah',
  [Dati2.BANTUL_KAB]: 'enums.dati2.bantulKab',
  [Dati2.SLEMAN_KAB]: 'enums.dati2.slemanKab',
  [Dati2.GUNUNG_KIDUL_KAB]: 'enums.dati2.gunungKidulKab',
  [Dati2.KULON_PROGO_KAB]: 'enums.dati2.kulonProgoKab',
  [Dati2.DI_YOGYAKARTA_KAB_KOTA_LAIN]: 'enums.dati2.diYogyakartaKabKotaLain',
  [Dati2.YOGYAKARTA_KOTA]: 'enums.dati2.yogyakartaKota',
  [Dati2.SEMARANG_KAB]: 'enums.dati2.semarangKab',
  [Dati2.KENDAL_KAB]: 'enums.dati2.kendalKab',
  [Dati2.DEMAK_KAB]: 'enums.dati2.demakKab',
  [Dati2.GROBOGAN_KAB]: 'enums.dati2.groboganKab',
  [Dati2.PEKALONGAN_KAB]: 'enums.dati2.pekalonganKab',
  [Dati2.TEGAL_KAB]: 'enums.dati2.tegalKab',
  [Dati2.BREBES_KAB]: 'enums.dati2.brebesKab',
  [Dati2.PATI_KAB]: 'enums.dati2.patiKab',
  [Dati2.KUDUS_KAB]: 'enums.dati2.kudusKab',
  [Dati2.PEMALANG_KAB]: 'enums.dati2.pemalangKab',
  [Dati2.JEPARA_KAB]: 'enums.dati2.jeparaKab',
  [Dati2.REMBANG_KAB]: 'enums.dati2.rembangKab',
  [Dati2.BLORA_KAB]: 'enums.dati2.bloraKab',
  [Dati2.BANYUMAS_KAB]: 'enums.dati2.banyumasKab',
  [Dati2.CILACAP_KAB]: 'enums.dati2.cilacapKab',
  [Dati2.PURBALINGGA_KAB]: 'enums.dati2.purbalinggaKab',
  [Dati2.BANJARNEGARA_KAB]: 'enums.dati2.banjarnegaraKab',
  [Dati2.MAGELANG_KAB]: 'enums.dati2.magelangKab',
  [Dati2.TEMANGGUNG_KAB]: 'enums.dati2.temanggungKab',
  [Dati2.WONOSOBO_KAB]: 'enums.dati2.wonosoboKab',
  [Dati2.PURWOREJO_KAB]: 'enums.dati2.purworejoKab',
  [Dati2.KEBUMEN_KAB]: 'enums.dati2.kebumenKab',
  [Dati2.KLATEN_KAB]: 'enums.dati2.klatenKab',
  [Dati2.BOYOLALI_KAB]: 'enums.dati2.boyolaliKab',
  [Dati2.SRAGEN_KAB]: 'enums.dati2.sragenKab',
  [Dati2.SUKOHARJO_KAB]: 'enums.dati2.sukoharjoKab',
  [Dati2.KARANGANYAR_KAB]: 'enums.dati2.karanganyarKab',
  [Dati2.WONOGIRI_KAB]: 'enums.dati2.wonogiriKab',
  [Dati2.BATANG_KAB]: 'enums.dati2.batangKab',
  [Dati2.PROV_JAWA_TENGAH_KAB_LAINNYA]: 'enums.dati2.provJawaTengahKabLainnya',
  [Dati2.SEMARANG_KOTA]: 'enums.dati2.semarangKota',
  [Dati2.SALATIGA_KOTA]: 'enums.dati2.salatigaKota',
  [Dati2.PEKALONGAN_KOTA]: 'enums.dati2.pekalonganKota',
  [Dati2.TEGAL_KOTA]: 'enums.dati2.tegalKota',
  [Dati2.MAGELANG_KOTA]: 'enums.dati2.magelangKota',
  [Dati2.SURAKARTA_KOTA]: 'enums.dati2.surakartaKota',
  [Dati2.GRESIK_KAB]: 'enums.dati2.gresikKab',
  [Dati2.SIDOARJO_KAB]: 'enums.dati2.sidoarjoKab',
  [Dati2.MOJOKERTO_KAB]: 'enums.dati2.mojokertoKab',
  [Dati2.JOMBANG_KAB]: 'enums.dati2.jombangKab',
  [Dati2.SAMPANG_KAB]: 'enums.dati2.sampangKab',
  [Dati2.PAMEKASAN_KAB]: 'enums.dati2.pamekasanKab',
  [Dati2.SUMENEP_KAB]: 'enums.dati2.sumenepKab',
  [Dati2.BANGKALAN_KAB]: 'enums.dati2.bangkalanKab',
  [Dati2.BONDOWOSO_KAB]: 'enums.dati2.bondowosoKab',
  [Dati2.BANYUWANGI_KAB]: 'enums.dati2.banyuwangiKab',
  [Dati2.JEMBER_KAB]: 'enums.dati2.jemberKab',
  [Dati2.MALANG_KAB]: 'enums.dati2.malangKab',
  [Dati2.PASURUAN_KAB]: 'enums.dati2.pasuruanKab',
  [Dati2.PROBOLINGGO_KAB]: 'enums.dati2.probolinggoKab',
  [Dati2.LUMAJANG_KAB]: 'enums.dati2.lumajangKab',
  [Dati2.KEDIRI_KAB]: 'enums.dati2.kediriKab',
  [Dati2.NGANJUK_KAB]: 'enums.dati2.nganjukKab',
  [Dati2.TULUNGAGUNG_KAB]: 'enums.dati2.tulungagungKab',
  [Dati2.TRENGGALEK_KAB]: 'enums.dati2.trenggalekKab',
  [Dati2.BLITAR_KAB]: 'enums.dati2.blitarKab',
  [Dati2.MADIUN_KAB]: 'enums.dati2.madiunKab',
  [Dati2.NGAWI_KAB]: 'enums.dati2.ngawiKab',
  [Dati2.MAGETAN_KAB]: 'enums.dati2.magetanKab',
  [Dati2.PONOROGO_KAB]: 'enums.dati2.ponorogoKab',
  [Dati2.PACITAN_KAB]: 'enums.dati2.pacitanKab',
  [Dati2.BOJONEGORO_KAB]: 'enums.dati2.bojonegoroKab',
  [Dati2.TUBAN_KAB]: 'enums.dati2.tubanKab',
  [Dati2.LAMONGAN_KAB]: 'enums.dati2.lamonganKab',
  [Dati2.SITUBONDO_KAB]: 'enums.dati2.situbondoKab',
  [Dati2.BATU_KOTA]: 'enums.dati2.batuKota',
  [Dati2.PROV_JAWA_TIMUR_KAB_KOTA_LAIN]: 'enums.dati2.provJawaTimurKabKotaLain',
  [Dati2.SURABAYA_KOTA]: 'enums.dati2.surabayaKota',
  [Dati2.MOJOKERTO_KOTA]: 'enums.dati2.mojokertoKota',
  [Dati2.MALANG_KOTA]: 'enums.dati2.malangKota',
  [Dati2.PASURUAN_KOTA]: 'enums.dati2.pasuruanKota',
  [Dati2.PROBOLINGGO_KOTA]: 'enums.dati2.probolinggoKota',
  [Dati2.BLITAR_KOTA]: 'enums.dati2.blitarKota',
  [Dati2.KEDIRI_KOTA]: 'enums.dati2.kediriKota',
  [Dati2.MADIUN_KOTA]: 'enums.dati2.madiunKota',
  [Dati2.BENGKULU_SELATAN_KAB]: 'enums.dati2.bengkuluSelatanKab',
  [Dati2.BENGKULU_UTARA_KAB]: 'enums.dati2.bengkuluUtaraKab',
  [Dati2.REJANG_LEBONG_KAB]: 'enums.dati2.rejangLebongKab',
  [Dati2.LEBONG_KAB]: 'enums.dati2.lebongKab',
  [Dati2.KEPAHIANG_KAB]: 'enums.dati2.kepahiangKab',
  [Dati2.MUKOMUKO_KAB]: 'enums.dati2.mukomukoKab',
  [Dati2.SELUMA_KAB]: 'enums.dati2.selumaKab',
  [Dati2.KAUR_KAB]: 'enums.dati2.kaurKab',
  [Dati2.KAB_BENGKULU_TENGAH]: 'enums.dati2.kabBengkuluTengah',
  [Dati2.PROV_BENGKULU_KAB_KOTA_LAIN]: 'enums.dati2.provBengkuluKabKotaLain',
  [Dati2.BENGKULU_KOTA]: 'enums.dati2.bengkuluKota',
  [Dati2.BATANGHARI_KAB]: 'enums.dati2.batanghariKab',
  [Dati2.SAROLANGUN_KAB]: 'enums.dati2.sarolangunKab',
  [Dati2.KERINCI_KAB]: 'enums.dati2.kerinciKab',
  [Dati2.MUARO_JAMBI_KAB]: 'enums.dati2.muaroJambiKab',
  [Dati2.TANJUNG_JABUNG_BARAT_KAB]: 'enums.dati2.tanjungJabungBaratKab',
  [Dati2.TANJUNG_JABUNG_TIMUR_KAB]: 'enums.dati2.tanjungJabungTimurKab',
  [Dati2.TEBO_KAB]: 'enums.dati2.teboKab',
  [Dati2.MERANGIN_KAB]: 'enums.dati2.meranginKab',
  [Dati2.BUNGO_KAB]: 'enums.dati2.bungoKab',
  [Dati2.PROV_JAMBI_KAB_KOTA_LAINNYA]: 'enums.dati2.provJambiKabKotaLainnya',
  [Dati2.JAMBI_KOTA]: 'enums.dati2.jambiKota',
  [Dati2.KOTA_SUNGAI_PENUH]: 'enums.dati2.kotaSungaiPenuh',
  [Dati2.ACEH_BESAR_KAB]: 'enums.dati2.acehBesarKab',
  [Dati2.PIDIE_KAB]: 'enums.dati2.pidieKab',
  [Dati2.ACEH_UTARA_KAB]: 'enums.dati2.acehUtaraKab',
  [Dati2.ACEH_TIMUR_KAB]: 'enums.dati2.acehTimurKab',
  [Dati2.ACEH_SELATAN_KAB]: 'enums.dati2.acehSelatanKab',
  [Dati2.ACEH_BARAT_KAB]: 'enums.dati2.acehBaratKab',
  [Dati2.ACEH_TENGAH_KAB]: 'enums.dati2.acehTengahKab',
  [Dati2.ACEH_TENGGARA_KAB]: 'enums.dati2.acehTenggaraKab',
  [Dati2.ACEH_SINGKIL_KAB]: 'enums.dati2.acehSingkilKab',
  [Dati2.ACEH_JEUMPA_BIREUEN_KAB]: 'enums.dati2.acehJeumpaBireuenKab',
  [Dati2.ACEH_TAMIANG_KAB]: 'enums.dati2.acehTamiangKab',
  [Dati2.GAYO_LUWES_KAB]: 'enums.dati2.gayoLuwesKab',
  [Dati2.ACEH_BARAT_DAYA_KAB]: 'enums.dati2.acehBaratDayaKab',
  [Dati2.ACEH_JAYA_KAB]: 'enums.dati2.acehJayaKab',
  [Dati2.NAGAN_RAYA_KAB]: 'enums.dati2.naganRayaKab',
  [Dati2.KAB_SIMEULEU]: 'enums.dati2.kabSimeuleu',
  [Dati2.KAB_BENER_MERIAH]: 'enums.dati2.kabBenerMeriah',
  [Dati2.PIDE_JAYA_KAB]: 'enums.dati2.pideJayaKab',
  [Dati2.SUBULUSSALAM]: 'enums.dati2.subulussalam',
  [Dati2.PROV_NAD_KAB_KOTA_LAINNYA]: 'enums.dati2.provNadKabKotaLainnya',
  [Dati2.BANDA_ACEH_KOTA]: 'enums.dati2.bandaAcehKota',
  [Dati2.SABANG_KOTA]: 'enums.dati2.sabangKota',
  [Dati2.LHOKSEUMAWE_KOTA]: 'enums.dati2.lhokseumaweKota',
  [Dati2.LANGSA_KOTA]: 'enums.dati2.langsaKota',
  [Dati2.DELI_SERDANG_KAB]: 'enums.dati2.deliSerdangKab',
  [Dati2.LANGKAT_KAB]: 'enums.dati2.langkatKab',
  [Dati2.KARO_KAB]: 'enums.dati2.karoKab',
  [Dati2.SIMALUNGUN_KAB]: 'enums.dati2.simalungunKab',
  [Dati2.LABUHAN_BATU_KAB]: 'enums.dati2.labuhanBatuKab',
  [Dati2.ASAHAN_KAB]: 'enums.dati2.asahanKab',
  [Dati2.DAIRI_KAB]: 'enums.dati2.dairiKab',
  [Dati2.TAPANULI_UTARA_KAB]: 'enums.dati2.tapanuliUtaraKab',
  [Dati2.TAPANULI_TENGAH_KAB]: 'enums.dati2.tapanuliTengahKab',
  [Dati2.TAPANULI_SELATAN_KAB]: 'enums.dati2.tapanuliSelatanKab',
  [Dati2.NIAS_KAB]: 'enums.dati2.niasKab',
  [Dati2.TOBA_SAMOSIR_KAB]: 'enums.dati2.tobaSamosirKab',
  [Dati2.MANDAILING_NATAL_KAB]: 'enums.dati2.mandailingNatalKab',
  [Dati2.NIAS_SELATAN_KAB]: 'enums.dati2.niasSelatanKab',
  [Dati2.HUMBANG_HASUNDUTAN_KAB]: 'enums.dati2.humbangHasundutanKab',
  [Dati2.PAKPAK_BARAT_KAB]: 'enums.dati2.pakpakBaratKab',
  [Dati2.SAMOSIR_KAB]: 'enums.dati2.samosirKab',
  [Dati2.SERDANG_BEDAGAI_KAB]: 'enums.dati2.serdangBedagaiKab',
  [Dati2.BATU_BARA_KAB]: 'enums.dati2.batuBaraKab',
  [Dati2.PADANG_LAWAS_KAB]: 'enums.dati2.padangLawasKab',
  [Dati2.PADANG_LAWAS_UTARA_KAB]: 'enums.dati2.padangLawasUtaraKab',
  [Dati2.KAB_LABUANBATU_SELATAN]: 'enums.dati2.kabLabuanbatuSelatan',
  [Dati2.KAB_LABUANBATU_UTARA]: 'enums.dati2.kabLabuanbatuUtara',
  [Dati2.KAB_NIAS_BARAT]: 'enums.dati2.kabNiasBarat',
  [Dati2.KAB_NIAS_UTARA]: 'enums.dati2.kabNiasUtara',
  [Dati2.PROV_SUMATERA_UTARA_KAB_LAIN]: 'enums.dati2.provSumateraUtaraKabLain',
  [Dati2.TEBING_TINGGI_KOTA]: 'enums.dati2.tebingTinggiKota',
  [Dati2.BINJAI_KOTA]: 'enums.dati2.binjaiKota',
  [Dati2.PEMATANG_SIANTAR_KOTA]: 'enums.dati2.pematangSiantarKota',
  [Dati2.TANJUNG_BALAI_KOTA]: 'enums.dati2.tanjungBalaiKota',
  [Dati2.SIBOLGA_KOTA]: 'enums.dati2.sibolgaKota',
  [Dati2.MEDAN_KOTA]: 'enums.dati2.medanKota',
  [Dati2.KOTA_GUNUNG_SITOLI]: 'enums.dati2.kotaGunungSitoli',
  [Dati2.PADANG_SIDEMPUAN_KOTA]: 'enums.dati2.padangSidempuanKota',
  [Dati2.AGAM_KAB]: 'enums.dati2.agamKab',
  [Dati2.PASAMAN_KAB]: 'enums.dati2.pasamanKab',
  [Dati2.LIMAPULUH_KOTO_KAB]: 'enums.dati2.limapuluhKotoKab',
  [Dati2.SOLOK_SELATAN_KAB]: 'enums.dati2.solokSelatanKab',
  [Dati2.PADANG_PARIAMAN_KAB]: 'enums.dati2.padangPariamanKab',
  [Dati2.PESISIR_SELATAN_KAB]: 'enums.dati2.pesisirSelatanKab',
  [Dati2.TANAH_DATAR_KAB]: 'enums.dati2.tanahDatarKab',
  [Dati2.KAB_SIJUNJUNG]: 'enums.dati2.kabSijunjung',
  [Dati2.KEPULAUAN_MENTAWAI_KAB]: 'enums.dati2.kepulauanMentawaiKab',
  [Dati2.PASAMAN_BARAT_KAB]: 'enums.dati2.pasamanBaratKab',
  [Dati2.DHARMASRAYA_KAB]: 'enums.dati2.dharmasrayaKab',
  [Dati2.SOLOK_KAB]: 'enums.dati2.solokKab',
  [Dati2.PROV_SUMATERA_BARAT_KAB_LAIN]: 'enums.dati2.provSumateraBaratKabLain',
  [Dati2.BUKITTINGGI_KOTA]: 'enums.dati2.bukittinggiKota',
  [Dati2.PADANG_KOTA]: 'enums.dati2.padangKota',
  [Dati2.SAWAHLUNTO_KOTA]: 'enums.dati2.sawahluntoKota',
  [Dati2.PADANG_PANJANG_KOTA]: 'enums.dati2.padangPanjangKota',
  [Dati2.SOLOK_KOTA]: 'enums.dati2.solokKota',
  [Dati2.PAYAKUMBUH_KOTA]: 'enums.dati2.payakumbuhKota',
  [Dati2.PARIAMAN_KOTA]: 'enums.dati2.pariamanKota',
  [Dati2.KAMPAR_KAB]: 'enums.dati2.kamparKab',
  [Dati2.BENGKALIS_KAB]: 'enums.dati2.bengkalisKab',
  [Dati2.INDRAGIRI_HULU_KAB]: 'enums.dati2.indragiriHuluKab',
  [Dati2.INDRAGIRI_HILIR_KAB]: 'enums.dati2.indragiriHilirKab',
  [Dati2.ROKAN_HULU_KAB]: 'enums.dati2.rokanHuluKab',
  [Dati2.ROKAN_HILIR_KAB]: 'enums.dati2.rokanHilirKab',
  [Dati2.PELALAWAN_KAB]: 'enums.dati2.pelalawanKab',
  [Dati2.SIAK_KAB]: 'enums.dati2.siakKab',
  [Dati2.KUANTAN_SINGINGI_KAB]: 'enums.dati2.kuantanSingingiKab',
  [Dati2.KAB_KEPULAUAN_MERANTI]: 'enums.dati2.kabKepulauanMeranti',
  [Dati2.PROV_RIAU_KAB_KOTA_LAINNYA]: 'enums.dati2.provRiauKabKotaLainnya',
  [Dati2.PEKANBARU_KOTA]: 'enums.dati2.pekanbaruKota',
  [Dati2.DUMAI_KOTA]: 'enums.dati2.dumaiKota',
  [Dati2.MUSI_BANYUASIN_KAB]: 'enums.dati2.musiBanyuasinKab',
  [Dati2.OGAN_KOMERING_ULU_KAB]: 'enums.dati2.oganKomeringUluKab',
  [Dati2.LEMATANG_ILIR_OGAN_TENGAH_MUARA_ENIM_KAB]: 'enums.dati2.lematangIlirOganTengahMuaraEnimKab',
  [Dati2.LAHAT_KAB]: 'enums.dati2.lahatKab',
  [Dati2.MUSI_RAWAS_KAB]: 'enums.dati2.musiRawasKab',
  [Dati2.OGAN_KOMERING_ILIR_KAB]: 'enums.dati2.oganKomeringIlirKab',
  [Dati2.BANYUASIN_KAB]: 'enums.dati2.banyuasinKab',
  [Dati2.OGAN_KOMERING_ULU_SELATAN_KAB]: 'enums.dati2.oganKomeringUluSelatanKab',
  [Dati2.OGAN_KOMERING_ULU_TIMUR_KAB]: 'enums.dati2.oganKomeringUluTimurKab',
  [Dati2.OGAN_ILIR_KAB]: 'enums.dati2.oganIlirKab',
  [Dati2.EMPAT_LAWANG]: 'enums.dati2.empatLawang',
  [Dati2.KAB_MUSI_RAWAS_UTARA]: 'enums.dati2.kabMusiRawasUtara',
  [Dati2.KAB_PENUKAL_ABAB_LEMATANG_ILIR]: 'enums.dati2.kabPenukalAbabLematangIlir',
  [Dati2.PROV_SUMATERA_SELATAN_LAINNYA]: 'enums.dati2.provSumateraSelatanLainnya',
  [Dati2.PALEMBANG_KOTA]: 'enums.dati2.palembangKota',
  [Dati2.LUBUKLINGGAU_KOTA]: 'enums.dati2.lubuklinggauKota',
  [Dati2.PRABUMULIH_KOTA]: 'enums.dati2.prabumulihKota',
  [Dati2.PAGAR_ALAM_KOTA]: 'enums.dati2.pagarAlamKota',
  [Dati2.BANGKA_KAB]: 'enums.dati2.bangkaKab',
  [Dati2.BELITUNG_KAB]: 'enums.dati2.belitungKab',
  [Dati2.BANGKA_BARAT_KAB]: 'enums.dati2.bangkaBaratKab',
  [Dati2.BANGKA_SELATAN_KAB]: 'enums.dati2.bangkaSelatanKab',
  [Dati2.BANGKA_TENGAH_KAB]: 'enums.dati2.bangkaTengahKab',
  [Dati2.BELITUNG_TIMUR_KAB]: 'enums.dati2.belitungTimurKab',
  [Dati2.KAB_BANGKA_BELITUNG]: 'enums.dati2.kabBangkaBelitung',
  [Dati2.PROV_BABEL_KAB_KOTA_LAINNYA]: 'enums.dati2.provBabelKabKotaLainnya',
  [Dati2.KARIMUN_KAB]: 'enums.dati2.karimunKab',
  [Dati2.LINGGA_KAB]: 'enums.dati2.linggaKab',
  [Dati2.NATUNA_KAB]: 'enums.dati2.natunaKab',
  [Dati2.BINTAN_KAB_D_H_KAB_KEPULAUAN_RIAU]: 'enums.dati2.bintanKabDHKabKepulauanRiau',
  [Dati2.KAB_KEPULAUAN_ANAMBAS]: 'enums.dati2.kabKepulauanAnambas',
  [Dati2.PROV_KEPULAUAN_RIAU_KAB_LAIN]: 'enums.dati2.provKepulauanRiauKabLain',
  [Dati2.TANJUNGPINANG_KOTA]: 'enums.dati2.tanjungpinangKota',
  [Dati2.BATAM_KOTA]: 'enums.dati2.batamKota',
  [Dati2.LAMPUNG_SELATAN_KAB]: 'enums.dati2.lampungSelatanKab',
  [Dati2.LAMPUNG_TENGAH_KAB]: 'enums.dati2.lampungTengahKab',
  [Dati2.LAMPUNG_UTARA_KAB]: 'enums.dati2.lampungUtaraKab',
  [Dati2.LAMPUNG_BARAT_KAB]: 'enums.dati2.lampungBaratKab',
  [Dati2.TULANG_BAWANG_KAB]: 'enums.dati2.tulangBawangKab',
  [Dati2.TANGGAMUS_KAB]: 'enums.dati2.tanggamusKab',
  [Dati2.LAMPUNG_TIMUR_KAB]: 'enums.dati2.lampungTimurKab',
  [Dati2.WAY_KANAN_KAB]: 'enums.dati2.wayKananKab',
  [Dati2.PESAWARAN_KAB]: 'enums.dati2.pesawaranKab',
  [Dati2.KAB_PRINGSEWU]: 'enums.dati2.kabPringsewu',
  [Dati2.KAB_TULANG_BAWANG_BARAT]: 'enums.dati2.kabTulangBawangBarat',
  [Dati2.KAB_MESUJI]: 'enums.dati2.kabMesuji',
  [Dati2.KAB_PESISIR_BARAT]: 'enums.dati2.kabPesisirBarat',
  [Dati2.PROV_LAMPUNG_KAB_KOTA_LAIN]: 'enums.dati2.provLampungKabKotaLain',
  [Dati2.BANDAR_LAMPUNG_KOTA]: 'enums.dati2.bandarLampungKota',
  [Dati2.METRO_KOTA]: 'enums.dati2.metroKota',
  [Dati2.BANJAR_KAB]: 'enums.dati2.banjarKab',
  [Dati2.TANAH_LAUT_KAB]: 'enums.dati2.tanahLautKab',
  [Dati2.TAPIN_KAB]: 'enums.dati2.tapinKab',
  [Dati2.HULU_SUNGAI_SELATAN_KAB]: 'enums.dati2.huluSungaiSelatanKab',
  [Dati2.HULU_SUNGAI_TENGAH_KAB]: 'enums.dati2.huluSungaiTengahKab',
  [Dati2.HULU_SUNGAI_UTARA_KAB]: 'enums.dati2.huluSungaiUtaraKab',
  [Dati2.BARITO_KUALA_KAB]: 'enums.dati2.baritoKualaKab',
  [Dati2.KOTA_BARU_KAB]: 'enums.dati2.kotaBaruKab',
  [Dati2.TABALONG_KAB]: 'enums.dati2.tabalongKab',
  [Dati2.TANAH_BUMBU_KAB]: 'enums.dati2.tanahBumbuKab',
  [Dati2.BALANGAN_KAB]: 'enums.dati2.balanganKab',
  [Dati2.PROV_KAL_SEL_KAB_KOTA_LAINNYA]: 'enums.dati2.provKalSelKabKotaLainnya',
  [Dati2.BANJARMASIN_KOTA]: 'enums.dati2.banjarmasinKota',
  [Dati2.BANJARBARU_KOTA]: 'enums.dati2.banjarbaruKota',
  [Dati2.KAB_MEMPAWAH]: 'enums.dati2.kabMempawah',
  [Dati2.SAMBAS_KAB]: 'enums.dati2.sambasKab',
  [Dati2.KETAPANG_KAB]: 'enums.dati2.ketapangKab',
  [Dati2.SANGGAU_KAB]: 'enums.dati2.sanggauKab',
  [Dati2.SINTANG_KAB]: 'enums.dati2.sintangKab',
  [Dati2.KAPUAS_HULU_KAB]: 'enums.dati2.kapuasHuluKab',
  [Dati2.BENGKAYANG_KAB]: 'enums.dati2.bengkayangKab',
  [Dati2.LANDAK_KAB]: 'enums.dati2.landakKab',
  [Dati2.SEKADAU_KAB]: 'enums.dati2.sekadauKab',
  [Dati2.MELAWI_KAB]: 'enums.dati2.melawiKab',
  [Dati2.KAYONG_UTARA_KAB]: 'enums.dati2.kayongUtaraKab',
  [Dati2.KUBU_RAYA_KAB]: 'enums.dati2.kubuRayaKab',
  [Dati2.PROV_KAL_BAR_KAB_KOTA_LAIN]: 'enums.dati2.provKalBarKabKotaLain',
  [Dati2.PONTIANAK_KOTA]: 'enums.dati2.pontianakKota',
  [Dati2.SINGKAWANG_KOTA]: 'enums.dati2.singkawangKota',
  [Dati2.KUTAI_KARTANEGARA_KAB]: 'enums.dati2.kutaiKartanegaraKab',
  [Dati2.BERAU_KAB]: 'enums.dati2.berauKab',
  [Dati2.PASIR_KAB]: 'enums.dati2.pasirKab',
  [Dati2.BULUNGAN_KAB]: 'enums.dati2.bulunganKab',
  [Dati2.KUTAI_BARAT_KAB]: 'enums.dati2.kutaiBaratKab',
  [Dati2.KUTAI_TIMUR_KAB]: 'enums.dati2.kutaiTimurKab',
  [Dati2.NUNUKAN_KAB]: 'enums.dati2.nunukanKab',
  [Dati2.MALINAU_KAB]: 'enums.dati2.malinauKab',
  [Dati2.PENAJAM_PASER_UTARA_KAB]: 'enums.dati2.penajamPaserUtaraKab',
  [Dati2.TANA_TIDUNG_KAB]: 'enums.dati2.tanaTidungKab',
  [Dati2.KAB_MAHAKAM_ULU]: 'enums.dati2.kabMahakamUlu',
  [Dati2.PROV_KAL_TIM_KAB_KOTA_LAINNYA]: 'enums.dati2.provKalTimKabKotaLainnya',
  [Dati2.SAMARINDA_KOTA]: 'enums.dati2.samarindaKota',
  [Dati2.BALIKPAPAN_KOTA]: 'enums.dati2.balikpapanKota',
  [Dati2.TARAKAN_KOTA]: 'enums.dati2.tarakanKota',
  [Dati2.BONTANG_KOTA]: 'enums.dati2.bontangKota',
  [Dati2.KAPUAS_KAB]: 'enums.dati2.kapuasKab',
  [Dati2.KOTAWARINGIN_BARAT_KAB]: 'enums.dati2.kotawaringinBaratKab',
  [Dati2.KOTAWARINGIN_TIMUR_KAB]: 'enums.dati2.kotawaringinTimurKab',
  [Dati2.MURUNG_RAYA_KAB]: 'enums.dati2.murungRayaKab',
  [Dati2.BARITO_TIMUR_KAB]: 'enums.dati2.baritoTimurKab',
  [Dati2.BARITO_SELATAN_KAB]: 'enums.dati2.baritoSelatanKab',
  [Dati2.GUNUNG_MAS_KAB]: 'enums.dati2.gunungMasKab',
  [Dati2.BARITO_UTARA_KAB]: 'enums.dati2.baritoUtaraKab',
  [Dati2.PULANG_PISAU_KAB]: 'enums.dati2.pulangPisauKab',
  [Dati2.SERUYAN_KAB]: 'enums.dati2.seruyanKab',
  [Dati2.KATINGAN_KAB]: 'enums.dati2.katinganKab',
  [Dati2.SUKAMARA_KAB]: 'enums.dati2.sukamaraKab',
  [Dati2.LAMANDAU_KAB]: 'enums.dati2.lamandauKab',
  [Dati2.PROV_KAL_TENG_KAB_KOTA_LAIN]: 'enums.dati2.provKalTengKabKotaLain',
  [Dati2.PALANGKARAYA_KOTA]: 'enums.dati2.palangkarayaKota',
  [Dati2.DONGGALA_KAB]: 'enums.dati2.donggalaKab',
  [Dati2.POSO_KAB]: 'enums.dati2.posoKab',
  [Dati2.PARIMO_BANGGAI_KAB]: 'enums.dati2.parimoBanggaiKab',
  [Dati2.TOLI_TOLI_KAB]: 'enums.dati2.toliToliKab',
  [Dati2.KAB_BANGGAI_KEPULAUAN]: 'enums.dati2.kabBanggaiKepulauan',
  [Dati2.MOROWALI_KAB]: 'enums.dati2.morowaliKab',
  [Dati2.BUOL_KAB]: 'enums.dati2.buolKab',
  [Dati2.TOJO_UNA_UNA_KAB]: 'enums.dati2.tojoUnaUnaKab',
  [Dati2.PARIGI_MOUTONG_KAB]: 'enums.dati2.parigiMoutongKab',
  [Dati2.KAB_SIGI]: 'enums.dati2.kabSigi',
  [Dati2.KAB_BANGGAI_LAUT]: 'enums.dati2.kabBanggaiLaut',
  [Dati2.KAB_MOROWALI_UTARA]: 'enums.dati2.kabMorowaliUtara',
  [Dati2.PROV_SULAWESI_TENGAH_LAINNYA]: 'enums.dati2.provSulawesiTengahLainnya',
  [Dati2.PALU_KOTA]: 'enums.dati2.paluKota',
  [Dati2.PINRANG_KAB]: 'enums.dati2.pinrangKab',
  [Dati2.GOWA_KAB]: 'enums.dati2.gowaKab',
  [Dati2.WAJO_KAB]: 'enums.dati2.wajoKab',
  [Dati2.BONE_KAB]: 'enums.dati2.boneKab',
  [Dati2.TANA_TORAJA_KAB]: 'enums.dati2.tanaTorajaKab',
  [Dati2.MAROS_KAB]: 'enums.dati2.marosKab',
  [Dati2.LUWU_KAB]: 'enums.dati2.luwuKab',
  [Dati2.SINJAI_KAB]: 'enums.dati2.sinjaiKab',
  [Dati2.BULUKUMBA_KAB]: 'enums.dati2.bulukumbaKab',
  [Dati2.BANTAENG_KAB]: 'enums.dati2.bantaengKab',
  [Dati2.JENEPONTO_KAB]: 'enums.dati2.jenepontoKab',
  [Dati2.KEPULAUAN_SELAYAR_KAB]: 'enums.dati2.kepulauanSelayarKab',
  [Dati2.TAKALAR_KAB]: 'enums.dati2.takalarKab',
  [Dati2.BARRU_KAB]: 'enums.dati2.barruKab',
  [Dati2.SIDENRENG_RAPPANG_KAB]: 'enums.dati2.sidenrengRappangKab',
  [Dati2.PANGKAJENE_KEPULAUAN_KAB]: 'enums.dati2.pangkajeneKepulauanKab',
  [Dati2.KAB_SOPPENG_D_H_WATANSOPPENG]: 'enums.dati2.kabSoppengDHWatansoppeng',
  [Dati2.ENREKANG_KAB]: 'enums.dati2.enrekangKab',
  [Dati2.KAB_LUWU_TIMUR_D_H_LUWU_SELATAN]: 'enums.dati2.kabLuwuTimurDHLuwuSelatan',
  [Dati2.LUWU_UTARA_KAB]: 'enums.dati2.luwuUtaraKab',
  [Dati2.KAB_TORAJA_UTARA]: 'enums.dati2.kabTorajaUtara',
  [Dati2.PROV_SULAWESI_SELATAN_LAINNYA]: 'enums.dati2.provSulawesiSelatanLainnya',
  [Dati2.MAKASSAR_KOTA]: 'enums.dati2.makassarKota',
  [Dati2.PARE_PARE_KOTA]: 'enums.dati2.parePareKota',
  [Dati2.PALOPO_KOTA]: 'enums.dati2.palopoKota',
  [Dati2.MINAHASA_KAB]: 'enums.dati2.minahasaKab',
  [Dati2.BOLAANG_MONGONDOW_KAB]: 'enums.dati2.bolaangMongondowKab',
  [Dati2.SANGIHE_KAB]: 'enums.dati2.sangiheKab',
  [Dati2.KEPULAUAN_TALAUD_KAB]: 'enums.dati2.kepulauanTalaudKab',
  [Dati2.MINAHASA_SELATAN_KAB]: 'enums.dati2.minahasaSelatanKab',
  [Dati2.MINAHASA_UTARA_KAB]: 'enums.dati2.minahasaUtaraKab',
  [Dati2.MINAHASA_TENGGARA_KAB]: 'enums.dati2.minahasaTenggaraKab',
  [Dati2.BOLAANG_MONGOUNDOW_UTARA_KAB]: 'enums.dati2.bolaangMongoundowUtaraKab',
  [Dati2.KEPULAUAN_SITARO_KAB]: 'enums.dati2.kepulauanSitaroKab',
  [Dati2.KAB_BOLAANG_MONGONDOW_SELATAN]: 'enums.dati2.kabBolaangMongondowSelatan',
  [Dati2.KAB_BOLAANG_MONGONDOW_TIMUR]: 'enums.dati2.kabBolaangMongondowTimur',
  [Dati2.SULAWESI_UTARA_KAB_KOTA_LAIN]: 'enums.dati2.sulawesiUtaraKabKotaLain',
  [Dati2.MENADO_KOTA]: 'enums.dati2.menadoKota',
  [Dati2.KOTAMOBAGU_KOTA]: 'enums.dati2.kotamobaguKota',
  [Dati2.BITUNG_KOTA]: 'enums.dati2.bitungKota',
  [Dati2.KOTA_TOMOHON]: 'enums.dati2.kotaTomohon',
  [Dati2.GORONTALO_KAB]: 'enums.dati2.gorontaloKab',
  [Dati2.BUALEMO_KAB]: 'enums.dati2.bualemoKab',
  [Dati2.BONEBOLANGO_KAB]: 'enums.dati2.bonebolangoKab',
  [Dati2.POHUWATO_KAB]: 'enums.dati2.pohuwatoKab',
  [Dati2.GORONTALO_UTARA_KAB]: 'enums.dati2.gorontaloUtaraKab',
  [Dati2.PROV_GORONTALO_KAB_LAINNYA]: 'enums.dati2.provGorontaloKabLainnya',
  [Dati2.GORONTALO_KOTA]: 'enums.dati2.gorontaloKota',
  [Dati2.POLEWALI_MANDAR_KAB]: 'enums.dati2.polewaliMandarKab',
  [Dati2.MAJENE_KAB]: 'enums.dati2.majeneKab',
  [Dati2.MAMASA_KAB]: 'enums.dati2.mamasaKab',
  [Dati2.MAMUJU_UTARA_KAB]: 'enums.dati2.mamujuUtaraKab',
  [Dati2.KAB_MAMUJU_TENGAH]: 'enums.dati2.kabMamujuTengah',
  [Dati2.KAB_MAMUJU]: 'enums.dati2.kabMamuju',
  [Dati2.PROV_SULAWESI_BARAT_LAINNYA]: 'enums.dati2.provSulawesiBaratLainnya',
  [Dati2.BUTON_KAB]: 'enums.dati2.butonKab',
  [Dati2.MUNA_KAB]: 'enums.dati2.munaKab',
  [Dati2.KOLAKA_KAB]: 'enums.dati2.kolakaKab',
  [Dati2.WAKATOBI_KAB]: 'enums.dati2.wakatobiKab',
  [Dati2.KONAWE_KAB]: 'enums.dati2.konaweKab',
  [Dati2.KONAWE_SELATAN_KAB]: 'enums.dati2.konaweSelatanKab',
  [Dati2.BOMBANA_KAB]: 'enums.dati2.bombanaKab',
  [Dati2.KOLAKA_UTARA_KAB]: 'enums.dati2.kolakaUtaraKab',
  [Dati2.BUTON_UTARA_KAB]: 'enums.dati2.butonUtaraKab',
  [Dati2.KONAWE_UTARA_KAB]: 'enums.dati2.konaweUtaraKab',
  [Dati2.KAB_KOLAKA_TIMUR]: 'enums.dati2.kabKolakaTimur',
  [Dati2.KAB_KONAWE_KEPULAUAN]: 'enums.dati2.kabKonaweKepulauan',
  [Dati2.KAB_BUTTON_SELATAN]: 'enums.dati2.kabButtonSelatan',
  [Dati2.KAB_BUTTON_TENGAH]: 'enums.dati2.kabButtonTengah',
  [Dati2.KAB_MUNA_BARAT]: 'enums.dati2.kabMunaBarat',
  [Dati2.PROV_SULAWESI_TENGGARA_LAINNYA]: 'enums.dati2.provSulawesiTenggaraLainnya',
  [Dati2.BAU_BAU_KOTA]: 'enums.dati2.bauBauKota',
  [Dati2.KENDARI_KOTA]: 'enums.dati2.kendariKota',
  [Dati2.LOMBOK_BARAT_KAB]: 'enums.dati2.lombokBaratKab',
  [Dati2.LOMBOK_TENGAH_KAB]: 'enums.dati2.lombokTengahKab',
  [Dati2.LOMBOK_TIMUR_KAB]: 'enums.dati2.lombokTimurKab',
  [Dati2.SUMBAWA_KAB]: 'enums.dati2.sumbawaKab',
  [Dati2.BIMA_KAB]: 'enums.dati2.bimaKab',
  [Dati2.DOMPU_KAB]: 'enums.dati2.dompuKab',
  [Dati2.SUMBAWA_BARAT_KAB]: 'enums.dati2.sumbawaBaratKab',
  [Dati2.KAB_LOMBOK_UTARA]: 'enums.dati2.kabLombokUtara',
  [Dati2.PROV_NTB_KAB_KOTA_LAINNYA]: 'enums.dati2.provNtbKabKotaLainnya',
  [Dati2.MATARAM_KOTA]: 'enums.dati2.mataramKota',
  [Dati2.KOTA_BIMA]: 'enums.dati2.kotaBima',
  [Dati2.BULELENG_KAB]: 'enums.dati2.bulelengKab',
  [Dati2.JEMBRANA_KAB]: 'enums.dati2.jembranaKab',
  [Dati2.TABANAN_KAB]: 'enums.dati2.tabananKab',
  [Dati2.BADUNG_KAB]: 'enums.dati2.badungKab',
  [Dati2.GIANYAR_KAB]: 'enums.dati2.gianyarKab',
  [Dati2.KLUNGKUNG_KAB]: 'enums.dati2.klungkungKab',
  [Dati2.BANGLI_KAB]: 'enums.dati2.bangliKab',
  [Dati2.KARANGASEM_KAB]: 'enums.dati2.karangasemKab',
  [Dati2.PROV_BALI_KAB_KOTA_LAINNYA]: 'enums.dati2.provBaliKabKotaLainnya',
  [Dati2.DENPASAR_KOTA]: 'enums.dati2.denpasarKota',
  [Dati2.KUPANG_KAB]: 'enums.dati2.kupangKab',
  [Dati2.TIMOR_TENGAH_SELATAN_KAB]: 'enums.dati2.timorTengahSelatanKab',
  [Dati2.TIMOR_TENGAH_UTARA_KAB]: 'enums.dati2.timorTengahUtaraKab',
  [Dati2.BELU_KAB]: 'enums.dati2.beluKab',
  [Dati2.ALOR_KAB]: 'enums.dati2.alorKab',
  [Dati2.FLORES_TIMUR_KAB]: 'enums.dati2.floresTimurKab',
  [Dati2.SIKKA_KAB]: 'enums.dati2.sikkaKab',
  [Dati2.ENDE_KAB]: 'enums.dati2.endeKab',
  [Dati2.NGADA_KAB]: 'enums.dati2.ngadaKab',
  [Dati2.MANGGARAI_KAB]: 'enums.dati2.manggaraiKab',
  [Dati2.SUMBA_TIMUR_KAB]: 'enums.dati2.sumbaTimurKab',
  [Dati2.SUMBA_BARAT_KAB]: 'enums.dati2.sumbaBaratKab',
  [Dati2.LEMBATA_KAB]: 'enums.dati2.lembataKab',
  [Dati2.ROTE_KAB]: 'enums.dati2.roteKab',
  [Dati2.MANGGARAI_BARAT_KAB]: 'enums.dati2.manggaraiBaratKab',
  [Dati2.SUMBA_TENGAH_KAB]: 'enums.dati2.sumbaTengahKab',
  [Dati2.SUMBA_BARAT_DAYA_KAB]: 'enums.dati2.sumbaBaratDayaKab',
  [Dati2.MANGGARAI_TIMUR_KAB]: 'enums.dati2.manggaraiTimurKab',
  [Dati2.NAGEKEO_KAB]: 'enums.dati2.nagekeoKab',
  [Dati2.KAB_SABU_RAIJUA]: 'enums.dati2.kabSabuRaijua',
  [Dati2.KAB_MALAKA]: 'enums.dati2.kabMalaka',
  [Dati2.PROV_NTT_KAB_KOTA_LAINNYA]: 'enums.dati2.provNttKabKotaLainnya',
  [Dati2.KUPANG_KOTA]: 'enums.dati2.kupangKota',
  [Dati2.MALUKU_TENGAH_KAB]: 'enums.dati2.malukuTengahKab',
  [Dati2.MALUKU_TENGGARA_KAB]: 'enums.dati2.malukuTenggaraKab',
  [Dati2.MALUKU_TENGGARA_BARAT_KAB]: 'enums.dati2.malukuTenggaraBaratKab',
  [Dati2.KAB_BURU]: 'enums.dati2.kabBuru',
  [Dati2.SERAM_BAGIAN_BARAT_KOTA]: 'enums.dati2.seramBagianBaratKota',
  [Dati2.SERAM_BAGIAN_TIMUR_KOTA]: 'enums.dati2.seramBagianTimurKota',
  [Dati2.KEPULAUAN_ARU_KOTA]: 'enums.dati2.kepulauanAruKota',
  [Dati2.KAB_MALUKU_BARAT_DAYA]: 'enums.dati2.kabMalukuBaratDaya',
  [Dati2.KAB_BURU_SELATAN]: 'enums.dati2.kabBuruSelatan',
  [Dati2.KAB_KOTA_LAINNYA]: 'enums.dati2.kabKotaLainnya',
  [Dati2.AMBON_KOTA]: 'enums.dati2.ambonKota',
  [Dati2.TUAL_KOTA]: 'enums.dati2.tualKota',
  [Dati2.JAYAPURA_KAB]: 'enums.dati2.jayapuraKab',
  [Dati2.BIAK_NUMFOR_KAB]: 'enums.dati2.biakNumforKab',
  [Dati2.YAPEN_WAROPEN_KAB]: 'enums.dati2.yapenWaropenKab',
  [Dati2.MERAUKE_KAB]: 'enums.dati2.meraukeKab',
  [Dati2.PANIAI_KAB]: 'enums.dati2.paniaiKab',
  [Dati2.JAYAWIJAYA_KAB]: 'enums.dati2.jayawijayaKab',
  [Dati2.NABIRE_KAB]: 'enums.dati2.nabireKab',
  [Dati2.MIMIKA_KAB]: 'enums.dati2.mimikaKab',
  [Dati2.PUNCAK_JAYA_KAB]: 'enums.dati2.puncakJayaKab',
  [Dati2.SARMI_KAB]: 'enums.dati2.sarmiKab',
  [Dati2.KEEROM_KAB]: 'enums.dati2.keeromKab',
  [Dati2.PEGUNUNGAN_BINTANG_KAB]: 'enums.dati2.pegununganBintangKab',
  [Dati2.YAHUKIMO_KAB]: 'enums.dati2.yahukimoKab',
  [Dati2.TOLIKARA_KAB]: 'enums.dati2.tolikaraKab',
  [Dati2.WAROPEN_KAB]: 'enums.dati2.waropenKab',
  [Dati2.BOVEN_DIGOEL_KAB]: 'enums.dati2.bovenDigoelKab',
  [Dati2.MAPPI_KAB]: 'enums.dati2.mappiKab',
  [Dati2.ASMAT_KAB]: 'enums.dati2.asmatKab',
  [Dati2.SUPIORI_KAB]: 'enums.dati2.supioriKab',
  [Dati2.MAMBERAMO_RAYA_KAB]: 'enums.dati2.mamberamoRayaKab',
  [Dati2.DOGIYAI_KAB]: 'enums.dati2.dogiyaiKab',
  [Dati2.LANNY_JAYA_KAB]: 'enums.dati2.lannyJayaKab',
  [Dati2.MAMBERAMO_TENGAH_KAB]: 'enums.dati2.mamberamoTengahKab',
  [Dati2.NDUGA_TENGAH_KAB]: 'enums.dati2.ndugaTengahKab',
  [Dati2.YALIMO_KAB]: 'enums.dati2.yalimoKab',
  [Dati2.PUNCAK_KAB]: 'enums.dati2.puncakKab',
  [Dati2.KAB_INTAN_JAYA]: 'enums.dati2.kabIntanJaya',
  [Dati2.KAB_DEIYAI]: 'enums.dati2.kabDeiyai',
  [Dati2.PROV_PAPUA_KAB_KOTA_LAINNYA]: 'enums.dati2.provPapuaKabKotaLainnya',
  [Dati2.JAYAPURA_KOTA]: 'enums.dati2.jayapuraKota',
  [Dati2.PROVINSI_MALUKU_UTARA]: 'enums.dati2.provinsiMalukuUtara',
  [Dati2.HALMAHERA_TENGAH_KAB]: 'enums.dati2.halmaheraTengahKab',
  [Dati2.HALMAHERA_UTARA_KAB]: 'enums.dati2.halmaheraUtaraKab',
  [Dati2.HALMAHERA_TIMUR_KAB]: 'enums.dati2.halmaheraTimurKab',
  [Dati2.HALMAHERA_BARAT_KAB]: 'enums.dati2.halmaheraBaratKab',
  [Dati2.HALMAHERA_SELATAN_KAB]: 'enums.dati2.halmaheraSelatanKab',
  [Dati2.KEPULAUAN_SULA_KAB]: 'enums.dati2.kepulauanSulaKab',
  [Dati2.KAB_PULAU_MOROTAI]: 'enums.dati2.kabPulauMorotai',
  [Dati2.KAB_PULAU_TALIABU]: 'enums.dati2.kabPulauTaliabu',
  [Dati2.PROV_MALUKU_UTARA_KAB_LAIN]: 'enums.dati2.provMalukuUtaraKabLain',
  [Dati2.TERNATE_KOTA]: 'enums.dati2.ternateKota',
  [Dati2.TIDORE_KEPULAUAN_KOTA]: 'enums.dati2.tidoreKepulauanKota',
  [Dati2.PROVINSI_IRIAN_JAYA_BARAT]: 'enums.dati2.provinsiIrianJayaBarat',
  [Dati2.SORONG_KAB]: 'enums.dati2.sorongKab',
  [Dati2.FAK_FAK_KAB]: 'enums.dati2.fakFakKab',
  [Dati2.MANOKWARI_KAB]: 'enums.dati2.manokwariKab',
  [Dati2.SORONG_SELATAN_KAB]: 'enums.dati2.sorongSelatanKab',
  [Dati2.RAJA_AMPAT_KAB]: 'enums.dati2.rajaAmpatKab',
  [Dati2.KAIMANA_KAB]: 'enums.dati2.kaimanaKab',
  [Dati2.TELUK_BINTUNI_KAB]: 'enums.dati2.telukBintuniKab',
  [Dati2.TELUK_WONDAMA_KAB]: 'enums.dati2.telukWondamaKab',
  [Dati2.KAB_TEMBRAUW]: 'enums.dati2.kabTembrauw',
  [Dati2.KAB_MAYBRAT]: 'enums.dati2.kabMaybrat',
  [Dati2.KAB_PEGUNUNGAN_ARFAK]: 'enums.dati2.kabPegununganArfak',
  [Dati2.KAB_MANOKWARI_SELATAN]: 'enums.dati2.kabManokwariSelatan',
  [Dati2.PROV_IRIAN_JAYA_BARAT_LAINNYA]: 'enums.dati2.provIrianJayaBaratLainnya',
  [Dati2.SORONG_KOTA]: 'enums.dati2.sorongKota',
  [Dati2.DI_LUAR_INDONESIA]: 'enums.dati2.diLuarIndonesia'
};

export const EntityTypeMap: Record<EntityType, string> = {
  [EntityType.FA]: 'enums.entityType.fa',
  [EntityType.CV]: 'enums.entityType.cv',
  [EntityType.PT]: 'enums.entityType.pt',
  [EntityType.PERSERO]: 'enums.entityType.persero',
  [EntityType.PERUM]: 'enums.entityType.perum',
  [EntityType.PERJAN]: 'enums.entityType.perjan',
  [EntityType.PERUSAHAAN_DAERAH]: 'enums.entityType.perusahaanDaerah',
  [EntityType.OTHER]: 'enums.entityType.other'
};

export const AccountPurposePurposeMap: Record<AccountPurposePurpose, string> = {
  [AccountPurposePurpose.SAVINGS]: 'enums.accountPurposePurpose.savings',
  [AccountPurposePurpose.WORKING_CAPITAL]: 'enums.accountPurposePurpose.workingCapital',
  [AccountPurposePurpose.INVESTMENT]: 'enums.accountPurposePurpose.investment',
  [AccountPurposePurpose.CONSUMPTION]: 'enums.accountPurposePurpose.consumption',
  [AccountPurposePurpose.BUSINESS_ACTIVITIES]: 'enums.accountPurposePurpose.businessActivities',
  [AccountPurposePurpose.LIFE_NECESSITIES]: 'enums.accountPurposePurpose.lifeNecessities',
  [AccountPurposePurpose.OTHER]: 'enums.accountPurposePurpose.other'
};

export const EntityMonthlyIncomeRangeMap: Record<EntityMonthlyIncomeRange, string> = {
  [EntityMonthlyIncomeRange.LT_25]: 'enums.entityMonthlyIncomeRange.lt25',
  [EntityMonthlyIncomeRange.GT_25_LTE_50]: 'enums.entityMonthlyIncomeRange.gt25Lte50',
  [EntityMonthlyIncomeRange.GT_50_LTE_100]: 'enums.entityMonthlyIncomeRange.gt50Lte100',
  [EntityMonthlyIncomeRange.GT_100_LTE_500]: 'enums.entityMonthlyIncomeRange.gt100Lte500',
  [EntityMonthlyIncomeRange.GT_500_LTE_1000]: 'enums.entityMonthlyIncomeRange.gt500Lte1000',
  [EntityMonthlyIncomeRange.GT_1000]: 'enums.entityMonthlyIncomeRange.gt1000'
};

export const PrimaryIncomeSourceTypeMap: Record<PrimaryIncomeSourceType, string> = {
  [PrimaryIncomeSourceType.COMPANY_EARNINGS]: 'enums.incomeSourceType.companyEarnings',
  [PrimaryIncomeSourceType.OTHER]: 'enums.incomeSourceType.other'
};

export const ManagementPositionMap: Record<ManagementPosition, string> = {
  [ManagementPosition.OWNER_MANAGEMENT]: 'enums.managementPosition.ownerManagement',
  [ManagementPosition.MANAGEMENT_PRESIDENT_DIRECTOR]: 'enums.managementPosition.managementPresidentDirector',
  [ManagementPosition.MANAGEMENT_DIRECTOR]: 'enums.managementPosition.managementDirector',
  [ManagementPosition.MANAGEMENT_PRESIDENT_COMMISSIONER]: 'enums.managementPosition.managementPresidentCommissioner',
  [ManagementPosition.MANAGEMENT_COMISSIONER]: 'enums.managementPosition.managementComissioner',
  [ManagementPosition.MANAGEMENT_BOARD_OF_DIRECTORS]: 'enums.managementPosition.managementBoardOfDirectors',
  [ManagementPosition.MANAGER_OWNER_NOT_MANAGEMENT]: 'enums.managementPosition.managerOwnerNotManagement',
  [ManagementPosition.MANAGEMENT_PUBLIC]: 'enums.managementPosition.managementPublic',
  [ManagementPosition.MANAGEMENT_GENERAL_CHAIRMAN]: 'enums.managementPosition.managementGeneralChairman',
  [ManagementPosition.OWNER_CHAIRMAN]: 'enums.managementPosition.ownerChairman',
  [ManagementPosition.OWNER_SECRETARY]: 'enums.managementPosition.ownerSecretary',
  [ManagementPosition.OWNER_TREASURER]: 'enums.managementPosition.ownerTreasurer',
  [ManagementPosition.OWNER_OTHER]: 'enums.managementPosition.ownerOther',
  [ManagementPosition.MANAGEMENT_NOT_OWNER]: 'enums.managementPosition.managementNotOwner',
  [ManagementPosition.NOT_OWNER_PRESIDENT_DIRECTOR]: 'enums.managementPosition.notOwnerPresidentDirector',
  [ManagementPosition.NOT_OWNER_DIRECTOR]: 'enums.managementPosition.notOwnerDirector',
  [ManagementPosition.NOT_OWNER_PRINCIPAL_COMMISSIONER]: 'enums.managementPosition.notOwnerPrincipalCommissioner',
  [ManagementPosition.NOT_OWNER_COMMISSIONER]: 'enums.managementPosition.notOwnerCommissioner',
  [ManagementPosition.NOT_OWNER_BOARD_OF_DIRECTORS]: 'enums.managementPosition.notOwnerBoardOfDirectors',
  [ManagementPosition.NOT_OWNER_GENERAL_CHAIRMAN]: 'enums.managementPosition.notOwnerGeneralChairman',
  [ManagementPosition.NOT_OWNER_CHAIRMAN]: 'enums.managementPosition.notOwnerChairman',
  [ManagementPosition.NOT_OWNER_SECRETARY]: 'enums.managementPosition.notOwnerSecretary',
  [ManagementPosition.NOT_OWNER_TREASURER]: 'enums.managementPosition.notOwnerTreasurer',
  [ManagementPosition.NOT_OWNER_OTHER]: 'enums.managementPosition.notOwnerOther'
};

export const GenderMap: Record<Gender, string> = {
  [Gender.MALE]: 'enums.gender.male',
  [Gender.FEMALE]: 'enums.gender.female'
};

export const MaritalStatusMap: Record<MaritalStatus, string> = {
  [MaritalStatus.UNMARRIED]: 'enums.maritalStatus.unmarried',
  [MaritalStatus.MARRIED]: 'enums.maritalStatus.married',
  [MaritalStatus.DIVORCED]: 'enums.maritalStatus.divorced',
  [MaritalStatus.WIDOWED]: 'enums.maritalStatus.widowed'
};

export const BeneficialOwnerJobTitleMap: Record<BeneficialOwnerJobTitle, string> = {
  [BeneficialOwnerJobTitle.ACCOUNTING_FINANCE]: 'enums.beneficialOwnerJobTitle.accountingFinance',
  [BeneficialOwnerJobTitle.CUSTOMER_SERVICE]: 'enums.beneficialOwnerJobTitle.customerService',
  [BeneficialOwnerJobTitle.ENGINEERING]: 'enums.beneficialOwnerJobTitle.engineering',
  [BeneficialOwnerJobTitle.EXECUTIVE]: 'enums.beneficialOwnerJobTitle.executive',
  [BeneficialOwnerJobTitle.GENERAL_ADMINISTRATION]: 'enums.beneficialOwnerJobTitle.generalAdministration',
  [BeneficialOwnerJobTitle.COMPUTER]: 'enums.beneficialOwnerJobTitle.computer',
  [BeneficialOwnerJobTitle.CONSULTANT]: 'enums.beneficialOwnerJobTitle.consultant',
  [BeneficialOwnerJobTitle.MARKETING]: 'enums.beneficialOwnerJobTitle.marketing',
  [BeneficialOwnerJobTitle.EDUCATION]: 'enums.beneficialOwnerJobTitle.education',
  [BeneficialOwnerJobTitle.GOVERNMENT]: 'enums.beneficialOwnerJobTitle.government',
  [BeneficialOwnerJobTitle.MILITARY]: 'enums.beneficialOwnerJobTitle.military',
  [BeneficialOwnerJobTitle.RETIRED]: 'enums.beneficialOwnerJobTitle.retired',
  [BeneficialOwnerJobTitle.STUDENT_OR_UNIVERSITY_STUDENT]: 'enums.beneficialOwnerJobTitle.studentOrUniversityStudent',
  [BeneficialOwnerJobTitle.ENTREPRENEUR]: 'enums.beneficialOwnerJobTitle.entrepreneur',
  [BeneficialOwnerJobTitle.POLICE]: 'enums.beneficialOwnerJobTitle.police',
  [BeneficialOwnerJobTitle.FARMER]: 'enums.beneficialOwnerJobTitle.farmer',
  [BeneficialOwnerJobTitle.FISHERMAN]: 'enums.beneficialOwnerJobTitle.fisherman',
  [BeneficialOwnerJobTitle.BREEDER]: 'enums.beneficialOwnerJobTitle.breeder',
  [BeneficialOwnerJobTitle.DOCTOR]: 'enums.beneficialOwnerJobTitle.doctor',
  [BeneficialOwnerJobTitle.MEDICAL_PERSONNEL]: 'enums.beneficialOwnerJobTitle.medicalPersonnel',
  [BeneficialOwnerJobTitle.LAW]: 'enums.beneficialOwnerJobTitle.law',
  [BeneficialOwnerJobTitle.HOSPITALITY_RESTAURANT]: 'enums.beneficialOwnerJobTitle.hospitalityRestaurant',
  [BeneficialOwnerJobTitle.RESEARCHER]: 'enums.beneficialOwnerJobTitle.researcher',
  [BeneficialOwnerJobTitle.DESIGNER]: 'enums.beneficialOwnerJobTitle.designer',
  [BeneficialOwnerJobTitle.ARCHITECT]: 'enums.beneficialOwnerJobTitle.architect',
  [BeneficialOwnerJobTitle.ART_WORKER]: 'enums.beneficialOwnerJobTitle.artWorker',
  [BeneficialOwnerJobTitle.SECURITY]: 'enums.beneficialOwnerJobTitle.security',
  [BeneficialOwnerJobTitle.MIDDLE_MAN_OR_BROKER]: 'enums.beneficialOwnerJobTitle.middleManOrBroker',
  [BeneficialOwnerJobTitle.DISTRIBUTOR]: 'enums.beneficialOwnerJobTitle.distributor',
  [BeneficialOwnerJobTitle.AIR_TRANSPORT]: 'enums.beneficialOwnerJobTitle.airTransport',
  [BeneficialOwnerJobTitle.WATER_TRANSPORT]: 'enums.beneficialOwnerJobTitle.waterTransport',
  [BeneficialOwnerJobTitle.LAND_TRANSPORT]: 'enums.beneficialOwnerJobTitle.landTransport',
  [BeneficialOwnerJobTitle.LABORER]: 'enums.beneficialOwnerJobTitle.laborer',
  [BeneficialOwnerJobTitle.ENGINEERING_CRAFT]: 'enums.beneficialOwnerJobTitle.engineeringCraft',
  [BeneficialOwnerJobTitle.HOUSEWIFE]: 'enums.beneficialOwnerJobTitle.housewife',
  [BeneficialOwnerJobTitle.INFORMAL_WORKER]: 'enums.beneficialOwnerJobTitle.informalWorker',
  [BeneficialOwnerJobTitle.NOT_USED]: 'enums.beneficialOwnerJobTitle.notUsed',
  [BeneficialOwnerJobTitle.OTHERS]: 'enums.beneficialOwnerJobTitle.others'
};

export const BeneficialOwnerJobMap: Record<BeneficialOwnerJob, string> = {
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_LEGISLATIVE_AND_GOVERNMENT_OFFICIALS]:
    'enums.beneficialOwnerJob.accountingFinanceLegislativeAndGovernmentOfficials',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_CIVIL_SERVANTS_INCLUDING_RETIREES_]:
    'enums.beneficialOwnerJob.accountingFinanceCivilServantsIncludingRetirees',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.accountingFinanceEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_POLITICAL_PARTY_MANAGER]:
    'enums.beneficialOwnerJob.accountingFinancePoliticalPartyManager',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_PRIVATE_EMPLOYEES]:
    'enums.beneficialOwnerJob.accountingFinancePrivateEmployees',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.accountingFinanceProfessionalsAndConsultants',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.accountingFinanceManagementAndEmployeesOfFoundationsOtherLegalEntities',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.accountingFinanceManagersEmployeesOfNgosOtherNonLegalEntities',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_BANK_EMPLOYEES]: 'enums.beneficialOwnerJob.accountingFinanceBankEmployees',
  [BeneficialOwnerJob.ACCOUNTING_FINANCE_MONEY_CHANGER]: 'enums.beneficialOwnerJob.accountingFinanceMoneyChanger',
  [BeneficialOwnerJob.CUSTOMER_SERVICE_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.customerServiceEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.CUSTOMER_SERVICE_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.customerServicePrivateEmployees',
  [BeneficialOwnerJob.CUSTOMER_SERVICE_BANK_EMPLOYEES]: 'enums.beneficialOwnerJob.customerServiceBankEmployees',
  [BeneficialOwnerJob.ENGINEERING_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.engineeringProfessionalsAndConsultants',
  [BeneficialOwnerJob.EXECUTIVES_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.executivesEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.EXECUTIVES_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.executivesPrivateEmployees',
  [BeneficialOwnerJob.EXECUTIVES_BANK_EMPLOYEES]: 'enums.beneficialOwnerJob.executivesBankEmployees',
  [BeneficialOwnerJob.EXECUTIVES_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.executivesManagementAndEmployeesOfFoundationsOtherLegalEntities',
  [BeneficialOwnerJob.GENERAL_ADMINISTRATION_CIVIL_SERVANTS_INCLUDING_RETIREES_]:
    'enums.beneficialOwnerJob.generalAdministrationCivilServantsIncludingRetirees',
  [BeneficialOwnerJob.EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.employeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.GENERAL_ADMINISTRATION_PRIVATE_EMPLOYEES]:
    'enums.beneficialOwnerJob.generalAdministrationPrivateEmployees',
  [BeneficialOwnerJob.GENERAL_ADMINISTRATION_POLITICAL_PARTY_MANAGER]:
    'enums.beneficialOwnerJob.generalAdministrationPoliticalPartyManager',
  [BeneficialOwnerJob.GENERAL_ADMINISTRATION_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.generalAdministrationManagementAndEmployeesOfFoundationsOtherLegalEntities',
  [BeneficialOwnerJob.GENERAL_ADMINISTRATION_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.generalAdministrationManagersEmployeesOfNgosOtherNonLegalEntities',
  [BeneficialOwnerJob.COMPUTER_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.computerProfessionalsAndConsultants',
  [BeneficialOwnerJob.CONSULTANT_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.consultantProfessionalsAndConsultants',
  [BeneficialOwnerJob.MARKETING_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.marketingEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.MARKETING_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.marketingPrivateEmployees',
  [BeneficialOwnerJob.MARKETING_BANK_EMPLOYEES]: 'enums.beneficialOwnerJob.marketingBankEmployees',
  [BeneficialOwnerJob.EDUCATION_TEACHERS_AND_LECTURERS]: 'enums.beneficialOwnerJob.educationTeachersAndLecturers',
  [BeneficialOwnerJob.GOVERNMENT_LEGISLATIVE_AND_GOVERNMENT_OFFICIALS]:
    'enums.beneficialOwnerJob.governmentLegislativeAndGovernmentOfficials',
  [BeneficialOwnerJob.GOVERNMENT_CIVIL_SERVANTS_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.governmentCivilServantsIncludingRetirees',
  [BeneficialOwnerJob.MILITARY_TNI_POLICE_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.militaryTniPoliceIncludingRetirees',
  [BeneficialOwnerJob.RETIRED_CIVIL_SERVANTS_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.retiredCivilServantsIncludingRetirees',
  [BeneficialOwnerJob.RETIRED_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.retiredEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.RETIRED_TNI_POLICE_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.retiredTniPoliceIncludingRetirees',
  [BeneficialOwnerJob.STUDENT_OR_UNIVERSITY_STUDENT_STUDENT_OR_UNIVERSITY_STUDENT]:
    'enums.beneficialOwnerJob.studentOrUniversityStudentStudentOrUniversityStudent',
  [BeneficialOwnerJob.ENTREPRENEUR_BUSINESSMAN_OR_ENTREPRENEUR]:
    'enums.beneficialOwnerJob.entrepreneurBusinessmanOrEntrepreneur',
  [BeneficialOwnerJob.POLICE_TNI_POLICE_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.policeTniPoliceIncludingRetirees',
  [BeneficialOwnerJob.FARMER_FARMERS_AND_FISHERMEN]: 'enums.beneficialOwnerJob.farmerFarmersAndFishermen',
  [BeneficialOwnerJob.FISHERMEN_FARMERS_AND_FISHERMEN]: 'enums.beneficialOwnerJob.fishermenFarmersAndFishermen',
  [BeneficialOwnerJob.BREEDER_BUSINESSMAN_OR_ENTREPRENEUR]: 'enums.beneficialOwnerJob.breederBusinessmanOrEntrepreneur',
  [BeneficialOwnerJob.DOCTOR_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.doctorProfessionalsAndConsultants',
  [BeneficialOwnerJob.MEDICAL_PERSONNEL_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.medicalPersonnelEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.MEDICAL_PERSONNEL_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.medicalPersonnelPrivateEmployees',
  [BeneficialOwnerJob.LAW_PROFESSIONALS_AND_CONSULTANTS]: 'enums.beneficialOwnerJob.lawProfessionalsAndConsultants',
  [BeneficialOwnerJob.HOSPITALITY_RESTAURANT_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.hospitalityRestaurantProfessionalsAndConsultants',
  [BeneficialOwnerJob.RESEARCHER_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.researcherProfessionalsAndConsultants',
  [BeneficialOwnerJob.DESIGNER_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.designerProfessionalsAndConsultants',
  [BeneficialOwnerJob.ARCHITECT_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.architectProfessionalsAndConsultants',
  [BeneficialOwnerJob.ARCHITECT_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.architectPrivateEmployees',
  [BeneficialOwnerJob.ART_WORKER_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.artWorkerProfessionalsAndConsultants',
  [BeneficialOwnerJob.SECURITY_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.securityEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.SECURITY_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.securityPrivateEmployees',
  [BeneficialOwnerJob.SECURITY_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS]:
    'enums.beneficialOwnerJob.securityWorkersHousekeepersAndSecurityWorkers',
  [BeneficialOwnerJob.MIDDLE_MAN_OR_BROKER_PROFESSIONALS_AND_CONSULTANTS]:
    'enums.beneficialOwnerJob.middleManOrBrokerProfessionalsAndConsultants',
  [BeneficialOwnerJob.MIDDLE_MAN_OR_BROKER_PRIVATE_EMPLOYEES]:
    'enums.beneficialOwnerJob.middleManOrBrokerPrivateEmployees',
  [BeneficialOwnerJob.DISTRIBUTOR_TRADER]: 'enums.beneficialOwnerJob.distributorTrader',
  [BeneficialOwnerJob.DISTRIBUTOR_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.distributorPrivateEmployees',
  [BeneficialOwnerJob.DISTRIBUTOR_BUSINESSMAN_OR_ENTREPRENEUR]:
    'enums.beneficialOwnerJob.distributorBusinessmanOrEntrepreneur',
  [BeneficialOwnerJob.AIR_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.airTransportEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.AIR_TRANSPORT_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.airTransportPrivateEmployees',
  [BeneficialOwnerJob.WATER_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.waterTransportEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.WATER_TRANSPORT_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.waterTransportPrivateEmployees',
  [BeneficialOwnerJob.LAND_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.landTransportEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.LAND_TRANSPORT_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.landTransportPrivateEmployees',
  [BeneficialOwnerJob.LABORER_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS]:
    'enums.beneficialOwnerJob.laborerWorkersHousekeepersAndSecurityWorkers',
  [BeneficialOwnerJob.ENGINEERING_CRAFT_CRAFTSMAN]: 'enums.beneficialOwnerJob.engineeringCraftCraftsman',
  [BeneficialOwnerJob.HOUSEWIFE_HOUSEWIFE]: 'enums.beneficialOwnerJob.housewifeHousewife',
  [BeneficialOwnerJob.INFORMAL_WORKER_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS]:
    'enums.beneficialOwnerJob.informalWorkerWorkersHousekeepersAndSecurityWorkers',
  [BeneficialOwnerJob.INFORMAL_WORKER_TRADER]: 'enums.beneficialOwnerJob.informalWorkerTrader',
  [BeneficialOwnerJob.OTHER_MONEY_CHANGER]: 'enums.beneficialOwnerJob.otherMoneyChanger',
  [BeneficialOwnerJob.OTHER_BANK_EMPLOYEES]: 'enums.beneficialOwnerJob.otherBankEmployees',
  [BeneficialOwnerJob.OTHER_ULAMA_PRIESTS_LEADERS_OF_ORGANIZATIONS_AND_RELIGIOUS_GROUPS]:
    'enums.beneficialOwnerJob.otherUlamaPriestsLeadersOfOrganizationsAndReligiousGroups',
  [BeneficialOwnerJob.OTHER_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.otherManagersEmployeesOfNgosOtherNonLegalEntities',
  [BeneficialOwnerJob.OTHER_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
    'enums.beneficialOwnerJob.otherManagementAndEmployeesOfFoundationsOtherLegalEntities',
  [BeneficialOwnerJob.OTHER_PRIVATE_EMPLOYEES]: 'enums.beneficialOwnerJob.otherPrivateEmployees',
  [BeneficialOwnerJob.OTHER_POLITICAL_PARTY_MANAGER]: 'enums.beneficialOwnerJob.otherPoliticalPartyManager',
  [BeneficialOwnerJob.OTHER_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
    'enums.beneficialOwnerJob.otherEmployeesBiBumnBumdIncludingRetirees',
  [BeneficialOwnerJob.OTHER_OTHER]: 'enums.beneficialOwnerJob.otherOther',
  [BeneficialOwnerJob.OTHER_TRACKING]: 'enums.beneficialOwnerJob.otherTracking'
};

export const BeneficialOwnerIncomeSourceMap: Record<BeneficialOwnerIncomeSourceType, string> = {
  [BeneficialOwnerIncomeSource.WAGES]: 'enums.beneficialOwnerIncomeSource.wages',
  [BeneficialOwnerIncomeSource.COMPANY_EARNINGS]: 'enums.beneficialOwnerIncomeSource.companyEarnings',
  [BeneficialOwnerIncomeSource.INHERITANCE]: 'enums.beneficialOwnerIncomeSource.inheritance',
  [BeneficialOwnerIncomeSource.DEPOSIT_INTEREST]: 'enums.beneficialOwnerIncomeSource.depositInterest',
  [BeneficialOwnerIncomeSource.OTHER]: 'enums.beneficialOwnerIncomeSource.other'
};

export const AuthorisedSignatoryPowerMap: Record<AuthorisedSignatoryPower, string> = {
  [AuthorisedSignatoryPower.CLOSE_ACCOUNT]: 'enums.authorisedSignatoryPower.closeAccount',
  [AuthorisedSignatoryPower.MANAGE_ACCOUNT_LINKING]: 'enums.authorisedSignatoryPower.manageAccountLinking',
  [AuthorisedSignatoryPower.VIEW_TRANSASCTION]: 'enums.authorisedSignatoryPower.viewTransaction',
  [AuthorisedSignatoryPower.MAKE_TRANSACTION]: 'enums.authorisedSignatoryPower.makeTransaction',
  [AuthorisedSignatoryPower.APPROVE_TRANSACTION]: 'enums.authorisedSignatoryPower.approveTransaction',
  [AuthorisedSignatoryPower.UPDATE_COMPANY_DETAILS]: 'enums.authorisedSignatoryPower.updateCompanyDetails',
  [AuthorisedSignatoryPower.RECEIVE_CALLBACK]: 'enums.authorisedSignatoryPower.receiveCallback'
};

export const AuthorisedSignatoriesTermsMap: Record<AuthorisedSignatoriesTerms, string> = {
  [AuthorisedSignatoriesTerms.SINGLE]: 'enums.authorisedSignatoriesTerms.single',
  [AuthorisedSignatoriesTerms.JOINT]: 'enums.authorisedSignatoriesTerms.joint'
};

export const IzinUsahaTypeMap: Record<IzinUsahaType, string> = {
  [IzinUsahaType.TDP]: 'enums.izinUsahaType.tdp',
  [IzinUsahaType.NIB]: 'enums.izinUsahaType.nib',
  [IzinUsahaType.SITU]: 'enums.izinUsahaType.situ',
  [IzinUsahaType.SIUP]: 'enums.izinUsahaType.siup'
};

export const SubmissionFilterMap: Record<GetPaginatedSubmissionsFilter, string> = {
  [GetPaginatedSubmissionsFilter.DMO_UNASSIGNED_NEW_CUSTOMER]: 'enums.submissionFilter.unassignedNew',
  [GetPaginatedSubmissionsFilter.DMO_UNASSIGNED_EXISTING_CUSTOMER]: 'enums.submissionFilter.unassignedExisting',
  [GetPaginatedSubmissionsFilter.DMS_UNASSIGNED_NEW_CUSTOMER]: 'enums.submissionFilter.unassignedNew',
  [GetPaginatedSubmissionsFilter.DMS_UNASSIGNED_EXISTING_CUSTOMER]: 'enums.submissionFilter.unassignedExisting',
  [GetPaginatedSubmissionsFilter.MY_IN_PROGRESS_NEW_CUSTOMER]: 'enums.submissionFilter.myNewInProgress',
  [GetPaginatedSubmissionsFilter.MY_IN_PROGRESS_EXISTING_CUSTOMER]: 'enums.submissionFilter.myExistingInProgress',
  [GetPaginatedSubmissionsFilter.MY_UPDATED_NEW_CUSTOMER]: 'enums.submissionFilter.myUpdatedNew',
  [GetPaginatedSubmissionsFilter.MY_UPDATED_EXISTING_CUSTOMER]: 'enums.submissionFilter.myUpdatedExisting',
  [GetPaginatedSubmissionsFilter.OUTSTANDING_DMO]: 'enums.submissionFilter.allOutstanding',
  [GetPaginatedSubmissionsFilter.OUTSTANDING_DMS]: 'enums.submissionFilter.allOutstanding',
  [GetPaginatedSubmissionsFilter.MY_OUTSTANDING_EDD]: 'enums.submissionFilter.approvedEddOutstanding',
  [GetPaginatedSubmissionsFilter.APPROVED]: 'status.approved',
  [GetPaginatedSubmissionsFilter.REJECTED]: 'status.rejected',
  [GetPaginatedSubmissionsFilter.MY_FINAL_IN_PROGRESS_NEW_CUSTOMER]: 'enums.submissionFilter.finalInProgressNew',
  [GetPaginatedSubmissionsFilter.MY_FINAL_IN_PROGRESS_EXISTING_CUSTOMER]:
    'enums.submissionFilter.finalInProgressExisting'
};

export const BeneficialOwnerJobTitleAndJob: Record<
  BeneficialOwnerJobTitle,
  Partial<Record<BeneficialOwnerJob, BeneficialOwnerJob>>
> = {
  [BeneficialOwnerJobTitle.ACCOUNTING_FINANCE]: {
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_LEGISLATIVE_AND_GOVERNMENT_OFFICIALS]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_LEGISLATIVE_AND_GOVERNMENT_OFFICIALS,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_CIVIL_SERVANTS_INCLUDING_RETIREES_]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_CIVIL_SERVANTS_INCLUDING_RETIREES_,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_POLITICAL_PARTY_MANAGER]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_POLITICAL_PARTY_MANAGER,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.ACCOUNTING_FINANCE_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_PROFESSIONALS_AND_CONSULTANTS,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES]:
      BeneficialOwnerJob.ACCOUNTING_FINANCE_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_BANK_EMPLOYEES]: BeneficialOwnerJob.ACCOUNTING_FINANCE_BANK_EMPLOYEES,
    [BeneficialOwnerJob.ACCOUNTING_FINANCE_MONEY_CHANGER]: BeneficialOwnerJob.ACCOUNTING_FINANCE_MONEY_CHANGER
  },
  [BeneficialOwnerJobTitle.CUSTOMER_SERVICE]: {
    [BeneficialOwnerJob.CUSTOMER_SERVICE_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.CUSTOMER_SERVICE_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.CUSTOMER_SERVICE_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.CUSTOMER_SERVICE_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.CUSTOMER_SERVICE_BANK_EMPLOYEES]: BeneficialOwnerJob.CUSTOMER_SERVICE_BANK_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.ENGINEERING]: {
    [BeneficialOwnerJob.ENGINEERING_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.ENGINEERING_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.EXECUTIVE]: {
    [BeneficialOwnerJob.EXECUTIVES_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.EXECUTIVES_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.EXECUTIVES_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.EXECUTIVES_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.EXECUTIVES_BANK_EMPLOYEES]: BeneficialOwnerJob.EXECUTIVES_BANK_EMPLOYEES,
    [BeneficialOwnerJob.EXECUTIVES_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
      BeneficialOwnerJob.EXECUTIVES_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES
  },
  [BeneficialOwnerJobTitle.GENERAL_ADMINISTRATION]: {
    [BeneficialOwnerJob.GENERAL_ADMINISTRATION_CIVIL_SERVANTS_INCLUDING_RETIREES_]:
      BeneficialOwnerJob.GENERAL_ADMINISTRATION_CIVIL_SERVANTS_INCLUDING_RETIREES_,
    [BeneficialOwnerJob.EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.GENERAL_ADMINISTRATION_PRIVATE_EMPLOYEES]:
      BeneficialOwnerJob.GENERAL_ADMINISTRATION_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.GENERAL_ADMINISTRATION_POLITICAL_PARTY_MANAGER]:
      BeneficialOwnerJob.GENERAL_ADMINISTRATION_POLITICAL_PARTY_MANAGER,
    [BeneficialOwnerJob.GENERAL_ADMINISTRATION_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
      BeneficialOwnerJob.GENERAL_ADMINISTRATION_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES,
    [BeneficialOwnerJob.GENERAL_ADMINISTRATION_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES]:
      BeneficialOwnerJob.GENERAL_ADMINISTRATION_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES
  },
  [BeneficialOwnerJobTitle.COMPUTER]: {
    [BeneficialOwnerJob.COMPUTER_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.COMPUTER_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.CONSULTANT]: {
    [BeneficialOwnerJob.CONSULTANT_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.CONSULTANT_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.MARKETING]: {
    [BeneficialOwnerJob.MARKETING_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.MARKETING_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.MARKETING_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.MARKETING_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.MARKETING_BANK_EMPLOYEES]: BeneficialOwnerJob.MARKETING_BANK_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.EDUCATION]: {
    [BeneficialOwnerJob.EDUCATION_TEACHERS_AND_LECTURERS]: BeneficialOwnerJob.EDUCATION_TEACHERS_AND_LECTURERS
  },
  [BeneficialOwnerJobTitle.GOVERNMENT]: {
    [BeneficialOwnerJob.GOVERNMENT_LEGISLATIVE_AND_GOVERNMENT_OFFICIALS]:
      BeneficialOwnerJob.GOVERNMENT_LEGISLATIVE_AND_GOVERNMENT_OFFICIALS,
    [BeneficialOwnerJob.GOVERNMENT_CIVIL_SERVANTS_INCLUDING_RETIREES]:
      BeneficialOwnerJob.GOVERNMENT_CIVIL_SERVANTS_INCLUDING_RETIREES
  },
  [BeneficialOwnerJobTitle.MILITARY]: {
    [BeneficialOwnerJob.MILITARY_TNI_POLICE_INCLUDING_RETIREES]:
      BeneficialOwnerJob.MILITARY_TNI_POLICE_INCLUDING_RETIREES
  },
  [BeneficialOwnerJobTitle.RETIRED]: {
    [BeneficialOwnerJob.RETIRED_CIVIL_SERVANTS_INCLUDING_RETIREES]:
      BeneficialOwnerJob.RETIRED_CIVIL_SERVANTS_INCLUDING_RETIREES,
    [BeneficialOwnerJob.RETIRED_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.RETIRED_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.RETIRED_TNI_POLICE_INCLUDING_RETIREES]: BeneficialOwnerJob.RETIRED_TNI_POLICE_INCLUDING_RETIREES
  },
  [BeneficialOwnerJobTitle.STUDENT_OR_UNIVERSITY_STUDENT]: {
    [BeneficialOwnerJob.STUDENT_OR_UNIVERSITY_STUDENT_STUDENT_OR_UNIVERSITY_STUDENT]:
      BeneficialOwnerJob.STUDENT_OR_UNIVERSITY_STUDENT_STUDENT_OR_UNIVERSITY_STUDENT
  },
  [BeneficialOwnerJobTitle.ENTREPRENEUR]: {
    [BeneficialOwnerJob.ENTREPRENEUR_BUSINESSMAN_OR_ENTREPRENEUR]:
      BeneficialOwnerJob.ENTREPRENEUR_BUSINESSMAN_OR_ENTREPRENEUR
  },
  [BeneficialOwnerJobTitle.POLICE]: {
    [BeneficialOwnerJob.POLICE_TNI_POLICE_INCLUDING_RETIREES]: BeneficialOwnerJob.CUSTOMER_SERVICE_BANK_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.FARMER]: {
    [BeneficialOwnerJob.FARMER_FARMERS_AND_FISHERMEN]: BeneficialOwnerJob.FARMER_FARMERS_AND_FISHERMEN
  },
  [BeneficialOwnerJobTitle.FISHERMAN]: {
    [BeneficialOwnerJob.FISHERMEN_FARMERS_AND_FISHERMEN]: BeneficialOwnerJob.FISHERMEN_FARMERS_AND_FISHERMEN
  },
  [BeneficialOwnerJobTitle.BREEDER]: {
    [BeneficialOwnerJob.BREEDER_BUSINESSMAN_OR_ENTREPRENEUR]: BeneficialOwnerJob.BREEDER_BUSINESSMAN_OR_ENTREPRENEUR
  },
  [BeneficialOwnerJobTitle.DOCTOR]: {
    [BeneficialOwnerJob.DOCTOR_PROFESSIONALS_AND_CONSULTANTS]: BeneficialOwnerJob.DOCTOR_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.MEDICAL_PERSONNEL]: {
    [BeneficialOwnerJob.MEDICAL_PERSONNEL_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.MEDICAL_PERSONNEL_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.MEDICAL_PERSONNEL_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.MEDICAL_PERSONNEL_PRIVATE_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.LAW]: {
    [BeneficialOwnerJob.LAW_PROFESSIONALS_AND_CONSULTANTS]: BeneficialOwnerJob.LAW_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.HOSPITALITY_RESTAURANT]: {
    [BeneficialOwnerJob.HOSPITALITY_RESTAURANT_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.HOSPITALITY_RESTAURANT_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.RESEARCHER]: {
    [BeneficialOwnerJob.RESEARCHER_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.RESEARCHER_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.DESIGNER]: {
    [BeneficialOwnerJob.DESIGNER_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.DESIGNER_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.ARCHITECT]: {
    [BeneficialOwnerJob.ARCHITECT_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.ARCHITECT_PROFESSIONALS_AND_CONSULTANTS,
    [BeneficialOwnerJob.ARCHITECT_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.ARCHITECT_PRIVATE_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.ART_WORKER]: {
    [BeneficialOwnerJob.ART_WORKER_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.ART_WORKER_PROFESSIONALS_AND_CONSULTANTS
  },
  [BeneficialOwnerJobTitle.SECURITY]: {
    [BeneficialOwnerJob.SECURITY_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.SECURITY_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.SECURITY_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.SECURITY_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.SECURITY_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS]:
      BeneficialOwnerJob.SECURITY_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS
  },
  [BeneficialOwnerJobTitle.MIDDLE_MAN_OR_BROKER]: {
    [BeneficialOwnerJob.MIDDLE_MAN_OR_BROKER_PROFESSIONALS_AND_CONSULTANTS]:
      BeneficialOwnerJob.MIDDLE_MAN_OR_BROKER_PROFESSIONALS_AND_CONSULTANTS,
    [BeneficialOwnerJob.MIDDLE_MAN_OR_BROKER_PRIVATE_EMPLOYEES]:
      BeneficialOwnerJob.MIDDLE_MAN_OR_BROKER_PRIVATE_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.DISTRIBUTOR]: {
    [BeneficialOwnerJob.DISTRIBUTOR_TRADER]: BeneficialOwnerJob.DISTRIBUTOR_TRADER,
    [BeneficialOwnerJob.DISTRIBUTOR_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.DISTRIBUTOR_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.DISTRIBUTOR_BUSINESSMAN_OR_ENTREPRENEUR]:
      BeneficialOwnerJob.DISTRIBUTOR_BUSINESSMAN_OR_ENTREPRENEUR
  },
  [BeneficialOwnerJobTitle.AIR_TRANSPORT]: {
    [BeneficialOwnerJob.AIR_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.AIR_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.AIR_TRANSPORT_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.AIR_TRANSPORT_PRIVATE_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.WATER_TRANSPORT]: {
    [BeneficialOwnerJob.WATER_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.WATER_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.WATER_TRANSPORT_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.WATER_TRANSPORT_PRIVATE_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.LAND_TRANSPORT]: {
    [BeneficialOwnerJob.LAND_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.LAND_TRANSPORT_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.LAND_TRANSPORT_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.LAND_TRANSPORT_PRIVATE_EMPLOYEES
  },
  [BeneficialOwnerJobTitle.LABORER]: {
    [BeneficialOwnerJob.LABORER_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS]:
      BeneficialOwnerJob.LABORER_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS
  },
  [BeneficialOwnerJobTitle.ENGINEERING_CRAFT]: {
    [BeneficialOwnerJob.ENGINEERING_CRAFT_CRAFTSMAN]: BeneficialOwnerJob.ENGINEERING_CRAFT_CRAFTSMAN
  },
  [BeneficialOwnerJobTitle.HOUSEWIFE]: {
    [BeneficialOwnerJob.HOUSEWIFE_HOUSEWIFE]: BeneficialOwnerJob.HOUSEWIFE_HOUSEWIFE
  },
  [BeneficialOwnerJobTitle.INFORMAL_WORKER]: {
    [BeneficialOwnerJob.INFORMAL_WORKER_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS]:
      BeneficialOwnerJob.INFORMAL_WORKER_WORKERS_HOUSEKEEPERS_AND_SECURITY_WORKERS,
    [BeneficialOwnerJob.INFORMAL_WORKER_TRADER]: BeneficialOwnerJob.INFORMAL_WORKER_TRADER
  },
  [BeneficialOwnerJobTitle.NOT_USED]: {
    [BeneficialOwnerJob.OTHER_OTHER]: BeneficialOwnerJob.OTHER_OTHER
  },
  [BeneficialOwnerJobTitle.OTHERS]: {
    [BeneficialOwnerJob.OTHER_MONEY_CHANGER]: BeneficialOwnerJob.OTHER_MONEY_CHANGER,
    [BeneficialOwnerJob.OTHER_BANK_EMPLOYEES]: BeneficialOwnerJob.OTHER_BANK_EMPLOYEES,
    [BeneficialOwnerJob.OTHER_ULAMA_PRIESTS_LEADERS_OF_ORGANIZATIONS_AND_RELIGIOUS_GROUPS]:
      BeneficialOwnerJob.OTHER_ULAMA_PRIESTS_LEADERS_OF_ORGANIZATIONS_AND_RELIGIOUS_GROUPS,
    [BeneficialOwnerJob.OTHER_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES]:
      BeneficialOwnerJob.OTHER_MANAGERS_EMPLOYEES_OF_NGOS_OTHER_NON_LEGAL_ENTITIES,
    [BeneficialOwnerJob.OTHER_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES]:
      BeneficialOwnerJob.OTHER_MANAGEMENT_AND_EMPLOYEES_OF_FOUNDATIONS_OTHER_LEGAL_ENTITIES,
    [BeneficialOwnerJob.OTHER_PRIVATE_EMPLOYEES]: BeneficialOwnerJob.OTHER_PRIVATE_EMPLOYEES,
    [BeneficialOwnerJob.OTHER_POLITICAL_PARTY_MANAGER]: BeneficialOwnerJob.OTHER_POLITICAL_PARTY_MANAGER,
    [BeneficialOwnerJob.OTHER_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES]:
      BeneficialOwnerJob.OTHER_EMPLOYEES_BI_BUMN_BUMD_INCLUDING_RETIREES,
    [BeneficialOwnerJob.OTHER_OTHER]: BeneficialOwnerJob.OTHER_OTHER,
    [BeneficialOwnerJob.OTHER_TRACKING]: BeneficialOwnerJob.OTHER_TRACKING
  }
};

export enum BeneficialOwnerAddressAnswer {
  YES = 'YES',
  NO = 'NO'
}

export const beneficialOwnerDifferentAddress: Record<BeneficialOwnerAddressAnswer, string> = {
  [BeneficialOwnerAddressAnswer.YES]: 'common.yes',
  [BeneficialOwnerAddressAnswer.NO]: 'common.no'
};
